import type { Department } from '@/types/user.types'
import type { RollupExperienceType } from '@/components/shared/ExperienceRollupCard'
import type {
  SaveViewPayload,
  SavedViewRecord,
  UpdateSavedViewPayload,
} from '@/types/savedView.types'

/**
 * Client mirror of the API's `GET /analytics/*` contract
 * (`apps/api/src/modules/analytics/types/analytics.types.ts`).
 *
 * Two rules, both load-bearing:
 *
 * 1. **Every figure is a number.** Formatting happens in components via
 *    `lib/formatters.ts` / `lib/stripe-formatters.ts`. A payload of display
 *    strings cannot be sorted (`'1,200' < '900'` lexically) and cannot be
 *    re-formatted for a chart axis.
 * 2. **`null` never means zero.** A median with no sample, or a conversion rate
 *    with nothing resolved, is `null` — rendering that as `0` would report an
 *    excellent cycle time or a total failure where there is simply no data.
 */

/**
 * Tabs on the Analytics console.
 *
 * `overview` is firm-wide and shows only to callers who can see every
 * department. `planners` and `specialists` are department tabs, offered per the
 * caller's own department — see `-components/analytics-department-tabs.ts`.
 *
 * **THE single source for this vocabulary.** Both route schemas build their
 * `tab` enum from this array (`-components/analytics-search-schema.ts` and
 * `users/$userId/index.tsx`), so adding or retiring a tab is one edit here and
 * the compiler finds the rest. It used to be written out three times — a union
 * plus two `z.enum` literals — and removing People, Advisors and Directors
 * meant changing all three in lockstep, which still left a spec fixture
 * holding a bare `'people'` string that `tsc` could not see because an
 * untyped string literal is not checked against anything.
 *
 * Service Queue is deliberately NOT here: it is a Link to the Forecasts route,
 * not a tab (see `-components/ServiceQueueLink.tsx`).
 */
export const ANALYTICS_TABS = ['overview', 'planners', 'specialists'] as const

export type AnalyticsTab = (typeof ANALYTICS_TABS)[number]

/**
 * Mirrors the API's `AnalyticsRange` exactly (`apps/api/.../analytics/types/
 * analytics.types.ts`). `custom` pairs with `from`/`to` on
 * {@link AnalyticsFilters}. Presets beyond these five — 7 days, quarter- and
 * year-to-date — are a client-only convenience that resolves to `custom` with
 * computed bounds; see `lib/analytics-range-presets.ts`.
 *
 * `month` is a whole CALENDAR month, resolved server-side — see the API's
 * `resolveMonthWindow` doc comment (`apps/api/.../analytics/lib/
 * date-buckets.ts`) for why this one range is never built client-side the way
 * `qtd`/`ytd` are: a month-over-month comparison needs the boundary exact to
 * the hour, and a browser-built boundary is wrong by the Denver offset. Pairs
 * with `month` (`YYYY-MM`) on {@link AnalyticsFilters}. The "This month" /
 * "Last month" presets resolve to this range, never to `custom` — see
 * `lib/analytics-range-presets.ts`.
 */
export type AnalyticsRange = '30d' | '90d' | '365d' | 'month' | 'custom'

export type AnalyticsBucket = 'day' | 'week' | 'month'

/** Resolved absolute bounds of the requested range. */
export interface AnalyticsWindow {
  from: string
  to: string
}

export interface AnalyticsFilters {
  range: AnalyticsRange
  /** ISO bounds for `range: 'custom'`. Ignored by the API otherwise. */
  from?: string
  to?: string
  /** `YYYY-MM` for `range: 'month'`, in the reporting zone. Ignored by the
   * API otherwise; omitted means the current calendar month. */
  month?: string
  /** Empty array = all experiences. */
  experience: Array<RollupExperienceType>
  /** Empty array = all staff departments. */
  department: Array<Department>
  /**
   * Empty array = all staff. User ids picked from `UserPicker`
   * (`multiple`/`staffOnly`) — sc-1212 replaced the old free-text "Search
   * staff" box with this because a fuzzy text match required knowing and
   * spelling a name to narrow anything and couldn't express "these two
   * people." Narrows the People roster AND every metric in `KpiMetricSelector` on a
   * department tab (Cases Ready, New plans worked, …) to entries attributed
   * to one of these ids — unlike the old `search`, which only ever touched
   * the People table.
   */
  person: Array<string>
}

/**
 * What a saved Analytics view captures (sc-1212): the tab's filter set,
 * exactly as it lives in the route's search params. Unlike Checkouts'
 * `CheckoutsSavedViewFilters`, this is a bare alias, not an `Omit` — the page
 * has no pagination field to strip.
 *
 * Deliberately the WHOLE of {@link AnalyticsFilters}, `person` included: a
 * click on the "By person" panel (`index.tsx`'s `togglePersonFilter`) writes
 * to `filters.person` through the exact same `setFilters` call the toolbar's
 * `UserPicker` uses — there is no separate "by-person scoping" value to
 * carve out, only the one filter field, however it was last set. What is
 * genuinely excluded is `selectedMetricKey`, the tile a manager currently has
 * open — that is `React.useState` on the route component, never written to
 * the URL (see ANALYTICS_DESIGN_STANDARD.md §9), so it was never part of
 * {@link AnalyticsFilters} to begin with and a saved view has no way to touch
 * it.
 */
export type AnalyticsSavedViewFilters = AnalyticsFilters

/** Analytics' instantiation of the generic saved-view shapes — see
 * `types/savedView.types.ts` and `billing.types.ts`'s Checkouts counterpart. */
export type AnalyticsSavedViewRecord =
  SavedViewRecord<AnalyticsSavedViewFilters>

export type SaveAnalyticsSavedViewPayload =
  SaveViewPayload<AnalyticsSavedViewFilters>

export type UpdateAnalyticsSavedViewPayload =
  UpdateSavedViewPayload<AnalyticsSavedViewFilters>

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export interface AnalyticsDataGathering {
  inProgress: number
  submitted: number
  approvedInRange: number
  oldestOpenAgeDays: number | null
  medianApprovalDays: number | null
}

export interface AnalyticsAnnualReviews {
  queued: number
  inProgress: number
  completedInRange: number
  dueNext30: number
  overdue: number
}

export interface AnalyticsRollupExperienceTypeRow {
  experience: RollupExperienceType
  open: number
  won: number
  lost: number
  wonValue: number
  lostValue: number
}

export interface AnalyticsCheckouts {
  open: number
  wonInRange: number
  lostInRange: number
  wonValue: number
  lostValue: number
  /** 0–1 fraction, or null when nothing resolved in the window. */
  conversionRate: number | null
  byExperience: Array<AnalyticsRollupExperienceTypeRow>
}

/**
 * `mrr`/`arr` are stocks; `collectedInRange` is a flow. Never scale them by the
 * same factor when the range changes — only the flow moves with the window.
 */
export interface AnalyticsRevenue {
  mrr: number
  arr: number
  activeSubscriptions: number
  pastDueCount: number
  cancelingCount: number
  collectedInRange: number
}

export interface AnalyticsBilling {
  needsCharge: number
  awaitingPayment: number
  failed: number
  chargedInRange: number
  queuedAmount: number
}

export type AnalyticsClientStatus =
  | 'INTERESTED'
  | 'PENDING'
  | 'ACTIVE'
  | 'INACTIVE'

export interface AnalyticsClients {
  total: number
  newInRange: number
  byStatus: Record<AnalyticsClientStatus, number>
  unassignedPlanner: number
}

export interface AnalyticsOverviewData {
  generatedAt: string
  window: AnalyticsWindow
  dataGathering: AnalyticsDataGathering
  annualReviews: AnalyticsAnnualReviews
  checkouts: AnalyticsCheckouts
  revenue: AnalyticsRevenue
  billing: AnalyticsBilling
  clients: AnalyticsClients
}

// ---------------------------------------------------------------------------
// Time series
// ---------------------------------------------------------------------------

export type AnalyticsSeriesKey =
  | 'dgApprovals'
  | 'checkoutsWon'
  | 'checkoutsLost'
  | 'paymentsCollected'
  | 'clientsAdded'

export interface AnalyticsPoint {
  bucketStart: string
  value: number
}

export interface AnalyticsSeries {
  key: AnalyticsSeriesKey
  /** True when `value` is dollars rather than a count. */
  isCurrency: boolean
  points: Array<AnalyticsPoint>
}

export interface AnalyticsTimeseriesData {
  window: AnalyticsWindow
  bucket: AnalyticsBucket
  timeZone: string
  series: Array<AnalyticsSeries>
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface AnalyticsPersonRow {
  userId: string
  firstName: string
  lastName: string
  email: string
  picture: string | null
  title: string | null
  department: Department
  clients: {
    asAdvisor: number
    asPlanner: number
    asSpecialist: number
  }
  dataGathering: {
    openForms: number
    approvedInRange: number
    medianApprovalDays: number | null
  }
  annualReviews: {
    queued: number
    started: number
    completedInRange: number
    medianCompletionDays: number | null
  }
  /**
   * `null` for a user with no HubSpot owner id — their checkouts cannot be
   * attributed, which is a different fact from having sold nothing. Render it
   * as "not attributable", never as 0.
   */
  checkouts: {
    wonInRange: number
    lostInRange: number
    wonValue: number
  } | null
}

export interface AnalyticsPersonActivity {
  formId: string
  action: string
  at: string
}

export interface AnalyticsPersonDetail extends AnalyticsPersonRow {
  window: AnalyticsWindow
  forecasts: {
    createdInRange: number
    escapePlansActivatedInRange: number
  }
  reports: {
    generatedInRange: number
  }
  recentActivity: Array<AnalyticsPersonActivity>
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

/**
 * Fixed-range labels only. `custom` has no single label — its display text is
 * the resolved preset (see `lib/analytics-range-presets.ts`) or the literal
 * date span, never this map.
 */
export const ANALYTICS_RANGE_LABELS: Record<AnalyticsRange, string> = {
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '365d': 'Last 12 months',
  // Generic fallback only — `month` normally reads through the "This month" /
  // "Last month" preset labels (`lib/analytics-range-presets.ts`), same as
  // `custom` normally reads through its own resolved preset rather than this
  // map.
  month: 'Calendar month',
  custom: 'Custom range',
}

export const ANALYTICS_EXPERIENCE_LABELS: Record<RollupExperienceType, string> =
  {
    ROTHOLOGY: 'Rothology',
    INTEGRATION_EXPERIENCE: 'Integration Experience',
    STRATEGIC_OVERSIGHT: 'Strategic Oversight',
  }

export const ANALYTICS_SERIES_LABELS: Record<AnalyticsSeriesKey, string> = {
  dgApprovals: 'DG approvals',
  checkoutsWon: 'Checkouts won',
  checkoutsLost: 'Checkouts lost',
  paymentsCollected: 'Payments collected',
  clientsAdded: 'Clients added',
}

/**
 * Specialist KPI 1 — overall productivity.
 *
 * Cases Ready — cases that reached "Ready for Presentation" in the Case
 * Design pipeline and are still there, or left it by finishing (Closed /
 * Refund Review Sent). Counts ENTRIES, not tickets: Case Design puts
 * "Revisions Needed" after the finish line, so a case can come back and be
 * moved again, and redoing that work is work. A case sent back for rework
 * (Alternative Scenarios / Additional Information / Revisions) is excluded
 * from this count entirely — it was never ready, so it never entered the
 * population "Cases Ready" claims.
 */
export interface ProductivityDay {
  /** YYYY-MM-DD in the reporting zone. */
  day: string
  count: number
  /**
   * That day's `count` split by `breakdown` key — same keys as
   * {@link AnalyticsProductivity.breakdown} (`Client` / `Planning Team` /
   * `Specialist Team` / `Other` / `Not set`). One query, grouped by day AND
   * source, produces both this and `breakdown`, so a day's split always sums
   * to that day's `count`, and summing every day's split always sums to
   * `breakdown` — that reconciliation is what makes the Errors addressed
   * panel's stacked Bar tab and Pie tab trustworthy against each other.
   *
   * `undefined`/`null` on a metric with no breakdown (every metric except
   * Errors addressed) AND on a day with no movement — a zero-fill day, not a
   * data gap. Render it as an empty stack (every series at 0), never throw
   * or draw a phantom zero-height segment as if it were a measured value.
   */
  byBreakdown?: Record<string, number> | null
}

export interface ProductivityPerson {
  userId: string
  firstName: string
  lastName: string
  email: string
  picture: string | null
  title: string | null
  department: Department
  count: number
}

export interface AnalyticsProductivity {
  /** How the counted stage is named back to the reader. */
  stageLabel: string
  /**
   * "From → To" for this KPI, generated by the API from the stage ids its own
   * query filters on. Rendered as a badge on the tile so a reader can tell
   * what a card counts without selecting it. Never written on the client — a
   * badge that drifts from the query is worse than no badge, because it is
   * what a reader checks the number against.
   */
  transitionLabel: string | null
  window: AnalyticsWindow
  timeZone: string
  total: number
  perDay: number
  /**
   * Moves whose owner is no longer staff, or was a workflow. Shown rather than
   * dropped so the daily total and the sum over people reconcile.
   */
  unattributed: number
  /** Latest entry into the stage overall, ignoring the window. */
  mostRecentEntryAt: string | null
  /**
   * Optional sub-categorisation of the same `total` — currently only Errors
   * addressed, split by the ticket's `change_source`.
   *
   * `null` for a metric with no meaningful split (Cases Ready, New plans
   * worked, Alternative scenarios completed) — not an empty array, because
   * "this metric isn't broken down" and "it's broken down and every bucket is
   * zero" must not render the same way. `KpiMetricSelector`'s chart-view
   * tabs (bar/pie) key off this being non-null, so a future transition KPI
   * only needs to set it server-side to get the same tabs for free.
   *
   * Buckets SUM TO `total`. `Not set` is included and will typically be the
   * largest bucket — most tickets state no cause — and must never be
   * dropped: a chart that doesn't reconcile with the headline above it is
   * worse than no chart.
   */
  breakdown: Array<ProductivityBreakdownBucket> | null
  /**
   * Whether one case can land in more than one `breakdown` bucket — decides
   * what a percentage of `breakdown` MEANS. Mirrors the API's
   * `BreakdownKind` (`apps/api/.../analytics/types/analytics.types.ts`).
   *
   * `'single'` — the underlying HubSpot property is a select (`change_source`
   * for Errors addressed): one value per ticket, so the buckets partition the
   * cases and sum to `total`. A slice is a share OF CASES.
   *
   * `'multi'` — the property is a checkbox (`missing_information` for
   * Information resolved): a case can flag several items, so the buckets
   * count ITEMS and deliberately exceed `total`. A slice is a share OF ITEMS
   * FLAGGED — reading it as a share of cases would produce percentages
   * adding past 100%, so any UI that labels a `breakdown` percentage must
   * branch on this field rather than assume `'single'`.
   *
   * `null` when `breakdown` is `null`.
   */
  breakdownKind: 'single' | 'multi' | null
  days: Array<ProductivityDay>
  people: Array<ProductivityPerson>
}

/** One slice of {@link AnalyticsProductivity.breakdown}. */
export interface ProductivityBreakdownBucket {
  /** Stable key — the raw `change_source` value, or `Other`/`Not set` for an
   * unrecognised or absent one. Colors and sort order are keyed off this,
   * never off array position — "color follows the entity, not its rank." */
  key: string
  label: string
  count: number
}

// ---------------------------------------------------------------------------
// Records audit (sc-1212) — "View records" on a KpiMetricSelector panel
// ---------------------------------------------------------------------------

/**
 * The `KpiMetricSelector` metrics (plus the orphaned backlog, sc-1212) this
 * endpoint can audit. Kept as its own union (not reused from
 * `KpiMetricEntry['key']`, which is a free-form string) because the wire
 * contract is fixed to exactly these values — see `GET /analytics/records`
 * in the API (`RECORDS_METRICS`/`RecordsMetric`).
 *
 * `'orphaned'` is not a `KpiMetricSelector` tile — it's the metric key the
 * unowned-backlog view (`OrphanedTicketsPanel`) passes when a stage bar is
 * clicked, alongside `stage` on the query. See `OrphanedTickets` below.
 */
export type AnalyticsRecordsMetric =
  | 'casesMoved'
  /**
   * Case Lifecycle's own drill-in. Separate from `casesMoved` because the two
   * count different things: Cases Ready counts ARRIVALS (a case that comes
   * back is a second event), while a lifecycle belongs to a TICKET. Sharing
   * one would list 280 rows for the lifecycle rollup's 197 tickets, 45 of them
   * duplicates with identical totals.
   */
  | 'caseLifecycle'
  | 'newPlansWorked'
  | 'alternativeScenariosCompleted'
  | 'errorsAddressed'
  | 'informationResolved'
  | 'alternativeScenariosRequested'
  /**
   * Revisions/Additional information RAISED (sc-1212 round 3) — the missing
   * "asked for" half of `errorsAddressed`/`informationResolved`, the same
   * relationship `alternativeScenariosRequested` already has to
   * `alternativeScenariosCompleted`. Drill-ins from the merged "Change
   * requests" tile (`KpiMetricSelector`) open these, never a headline tile
   * of their own — see `ChangeRequestRollup`'s doc comment for why the four
   * old per-kind tiles were merged into one.
   */
  | 'revisionsRequested'
  | 'informationRequested'
  | 'casesPulledBackToDataEntry'
  | 'orphaned'
  | 'epOutcomes'
  | 'planWork'

/**
 * Which segment of the Plan Delivery split a records request wants.
 *
 * `pending` is excluded on purpose — it never appears as a bar, tile, or
 * filter, only as a caption. `lateApproval` (sc-1212, 2026-09-29) IS
 * included: the records metric accepts `outcome=lateApproval` and
 * `EpOutcomesPanel`'s fourth tile drills into it exactly like the other
 * three.
 */
export type EpOutcomeFilter = 'onTime' | 'late' | 'missed' | 'lateApproval'

/**
 * A client's Plan Delivery verdict. `pending` never appears as a bar or a
 * filter — it is a caption saying how many verdicts are not due yet.
 *
 * **The `Ep`/`ep` prefix throughout this file is historical.** This metric
 * was called Escape Plan delivery until it was re-anchored onto the client's
 * first delivery meeting (sc-1212, 2026-09-25); identifiers kept their names
 * so the rename stayed a copy change. It has nothing to do with the Escape
 * Plan PDF report or the `escapePlansGenerated` column beside it on the
 * Specialists table.
 *
 * **`lateApproval` (sc-1212, 2026-09-29) is a fifth state, and it REPLACES
 * the verdict rather than sitting beside it.** It means the Case Design
 * ticket was created on the calendar day of the client's held presentation,
 * or later — approving the data-gathering form is what creates the ticket,
 * so this means nobody approved the form until the client was already in the
 * room. No specialist could have built anything: there was no case to grade.
 * Worked example: Cindy Banchy's documents sat from 31 August; her
 * presentation was held 23 September at 20:00; Marc Russell (her planner)
 * approved the form at 20:57; the ticket was created 17 seconds later, owned
 * by a specialist (Ryan Boyle) who never had the case. It is a PLANNER miss,
 * never folded into `missed` — see `SpecialistPersonRow.lateApprovalEp`.
 */
export type EpOutcome =
  | 'onTime'
  | 'late'
  | 'missed'
  | 'pending'
  | 'lateApproval'

export interface AnalyticsRecordRow {
  /** HubSpot ticket object id. */
  ticketId: string
  subject: string | null
  /** Deep link to the ticket in HubSpot. Null when the portal id isn't
   * configured — render no link, never a broken one. */
  hubspotUrl: string | null
  /** When the counted movement happened. */
  occurredAt: string
  /** Where the counted move came from. Null for `casesMoved`, which is
   * stamped-column based and has no source stage — render that honestly
   * (just the destination), not a dash that looks like missing data. */
  previousStageLabel: string | null
  /** Where the counted move went. Identical on every row of a given metric. */
  movedToStageLabel: string
  /** Where the ticket sits TODAY — often not where the counted move left it.
   * A case counted as e.g. a new plan worked and since bounced to Revisions
   * is exactly the oddity this audit exists to surface, so a row where this
   * differs from `movedToStageLabel` must read differently, not silently. */
  currentStageLabel: string | null
  /**
   * The same stage as HubSpot's raw stage id, because deciding "did this
   * case move backward" is REASONING, not display — the API's
   * `AnalyticsRecordRow` (`apps/api/.../types/analytics-records.types.ts`)
   * added this field for exactly that: comparing `currentStageLabel` display
   * strings would silently stop working the moment a stage got renamed in
   * HubSpot, where the id never changes. `TicketTimeDetail`'s
   * `CurrentStatusValue` uses this, not the label, to decide whether a
   * diverged case moved backward (flag) or forward by finishing (don't).
   */
  currentStageId: string | null
  /** Time in every Case Design stage, in HubSpot's display order, Closed
   * included. Null when the ticket has not been synced for durations yet —
   * different from a ticket that spent no time anywhere, and must read
   * differently (see `TicketTimeDetail`). */
  stageDurations: Array<{ stageId: string; label: string; ms: number }> | null
  /** Summed stage time EXCLUDING Closed, whose timer never stops once a case
   * closes. Null when durations have not been synced. */
  specialistTimeMs: number | null
  /**
   * Time this ticket spent back in RC Data Entry after work had already
   * started, caused by a bug in the form-approval flow rewriting the stage
   * unconditionally — **already SUBTRACTED from `specialistTimeMs`**, and
   * carried here so the records view can explain why a ticket's per-stage
   * bars (from `stageDurations`, which is raw and NOT netted of this) do not
   * sum to `specialistTimeMs` when it's non-zero. 54 tickets carry some on
   * production: median 2.1 days, worst 38.8. Render it visibly (muted,
   * labelled as excluded), never silently — an unexplained bars-don't-sum
   * discrepancy reads as a bug, not a deliberate exclusion.
   */
  spuriousDataEntryMs: number
  /**
   * Whose error caused the revision — `Client`, `Planning Team`,
   * `Specialist Team`, or `null` when the ticket states no cause (most of
   * them; render as such, never as a bare dash implying missing data).
   *
   * One current value per TICKET, not per revision: HubSpot keeps a single
   * `change_source`, so a case revised twice for different reasons keeps
   * only the latest — this is the ticket's most recent stated cause, not
   * necessarily the cause of the specific move this row records. Load-
   * bearing for anyone auditing the Errors addressed pie against this table.
   */
  changeSource: string | null
  /**
   * What the ticket's `missing_information` checkbox has flagged, RAW —
   * HubSpot joins a multi-select checkbox's selections with `;`
   * ("Sales Notes;Tax Planning & Payments Notes Missing"), un-split. `null`
   * when nothing is flagged (most rows; render as such, never a bare dash).
   *
   * NOT YET POPULATED BY THE API as of sc-1212's Information resolved tile —
   * `AnalyticsRecordsService` does not select or return `missing_information`
   * on this row yet, so every row reads `null` until that's added (select
   * `missing_information` alongside `change_source` and map it through, same
   * two spots `changeSource` is threaded today). The client already renders
   * this field (split on `;` into badges) so it lights up with no further
   * frontend change once the API sends it.
   */
  missingInformation: string | null
  /**
   * HubSpot `hs_ticket_priority` (sc-1212) — `LOW` / `MEDIUM` / `HIGH` /
   * `URGENT`, or `null` when unset. Sort by severity (Urgent > High > Medium
   * > Low, nulls last), never alphabetically — `KpiRecordsDialog`'s
   * `sortValue` ranks it rather than string-comparing, since alphabetical
   * order would put High above Urgent.
   */
  priority: string | null

  /**
   * Plan Delivery — populated ONLY when `metric === 'epOutcomes'`, null on
   * every other metric.
   *
   * Unlike `changeSource`, `missingInformation` and `priority`, which are plain
   * ticket columns the dialog renders for every metric, these are DERIVED:
   * filling them means resolving each client's first delivery meeting and
   * first stage arrival. That cost belongs to the one metric that asked for
   * it, so the dialog renders these columns for `epOutcomes` alone.
   */
  epFirstMeetingAt: string | null
  epReadyAt: string | null
  /** Business days of lead. NEGATIVE means the plan landed AFTER the meeting. */
  epLeadBusinessDays: number | null
  epOutcome: EpOutcome | null

  /**
   * Plan Work, populated ONLY for the `planWork` metric — derived like the
   * Plan Delivery fields above and gated in the dialog for the same reason.
   *
   * `planWorkForecastAt` was removed (sc-1212): Plan Work no longer measures
   * from a forecast date, so there is nothing for that field to carry.
   */
  planWorkSpanMs: number | null

  /** Null = not attributable to known staff. The gap between `total` and the
   * sum of `people` on the metric this audits is exactly this. */
  owner: {
    userId: string
    firstName: string
    lastName: string
    email: string
    picture: string | null
  } | null
  client: { id: string; name: string } | null
}

export interface AnalyticsRecords {
  metric: string
  stageLabel: string
  window: AnalyticsWindow
  /** Total matching rows, across EVERY page. */
  total: number
  /**
   * True when the server refused to send everything even across pages (a
   * hard cap), NOT when there is simply another page to fetch. Paging and
   * truncation mean different things and must not be conflated in copy —
   * see `KpiRecordsDialog`'s truncation banner, which only renders off this
   * field, never off `page`/`pageCount`.
   */
  truncated: boolean
  /** 1-based page this response represents. */
  page: number
  pageSize: number
  /** Total pages at this `pageSize`; 1 when there are no rows. */
  pageCount: number
  rows: Array<AnalyticsRecordRow>
}

// ---------------------------------------------------------------------------
// Case work kind (sc-1212 round 5) — mirrors the API's `CaseWorkKind`
// (`apps/api/.../analytics/lib/case-work-kind.ts`) exactly.
// ---------------------------------------------------------------------------

/**
 * What KIND of work a Case Design ticket is.
 *
 * Rothology is a one-time service. After it the firm sells ongoing services —
 * Integration Experience and Financial Planning — and that work arrives as
 * tickets on the SAME Case Design pipeline, so every number on this console
 * already counts it, unlabelled, folded into "Rothology".
 *
 * **Declaration order is PRECEDENCE** — the first whose `service_type` token
 * matches wins, so every case resolves to exactly one kind. Annual Review
 * comes first so `isAnnualReview === (kind === 'ANNUAL_REVIEW')` holds by
 * construction; Financial Planning outranks Rothology so a ticket carrying
 * both tokens reads as Financial Planning, not Rothology-only.
 *
 * `UNCLASSIFIED` is a REAL bucket (352 live cases: 162 Orphaned + 190 with no
 * `service_type` at all) — never fold it into Rothology.
 */
export const CASE_WORK_KINDS = [
  'ANNUAL_REVIEW',
  'FINANCIAL_PLANNING',
  'ROTHOLOGY',
  'UNCLASSIFIED',
] as const

export type CaseWorkKind = (typeof CASE_WORK_KINDS)[number]

/**
 * `CaseWorkKind` display copy. **User-facing copy is always "Financial
 * Planning"** (Tyler's word, 2026-09-30) — never "Ongoing", never "IE/FP" —
 * even though the wire value covers Integration Experience and Financial
 * Planning tokens together.
 *
 * Every picker builds its options from `Object.entries(...)` of this record,
 * same convention as `ANALYTICS_EXPERIENCE_LABELS` above — never a
 * hand-written options array that can drift from the enum.
 */
export const ANALYTICS_WORK_KIND_LABELS: Record<CaseWorkKind, string> = {
  ANNUAL_REVIEW: 'Annual Review',
  FINANCIAL_PLANNING: 'Financial Planning',
  ROTHOLOGY: 'Rothology',
  UNCLASSIFIED: 'Unclassified',
}

// ---------------------------------------------------------------------------
// Specialist people (sc-1212 follow-up) — GET /analytics/specialist-people
// ---------------------------------------------------------------------------

/**
 * One specialist's row in the Specialists tab's people table. Mirrors the
 * API's `SpecialistPersonRow`
 * (`apps/api/.../analytics/types/specialist-people.types.ts`) exactly.
 *
 * Its own endpoint/shape rather than columns bolted onto
 * `AnalyticsPersonRow` — that row is firm-wide (client counts, data
 * gathering, annual reviews, checkouts) and shares no sources with
 * specialist throughput.
 */
export interface SpecialistPersonRow {
  userId: string
  firstName: string
  lastName: string
  email: string
  picture: string | null
  title: string | null

  /** Forecasts this person created inside the window. */
  forecastsCreated: number

  /** Forecasts this person activated as an escape plan inside the window.
   * Attributed to whoever ACTIVATED it, not whoever created the forecast —
   * frequently a different person, so this is never a subset of
   * `forecastsCreated`. */
  escapePlansGenerated: number

  /**
   * "Plan Work" — median ms a case spent in the stages AFTER data entry
   * (Data Entry Complete, Planning in Progress, Additional Information
   * Needed, Revisions Needed, Alternative Scenarios Needed).
   *
   * **Attributed to the ticket OWNER**, like every other column here. Until
   * sc-1212 this was attributed to whoever raised the case's first forecast,
   * which made it the one column on this table answering a different
   * question from its neighbours — that is no longer true.
   *
   * Case Lifecycle minus the queue, so it can never exceed Case Lifecycle:
   * one pane is a subset of the other's population, not a different one.
   *
   * `null` when no case in the window reached that phase — NOT the same as
   * zero. Render with `formatDurationMs`, and check for `null` before
   * assuming a real duration.
   */
  planWorkMedianMs: number | null

  /**
   * Cases behind the median that REACHED the plan phase — a median of one is
   * not a median, and this is coverage, not a count of every case. On
   * production 530 of 731 cases reach the phase; always show this next to
   * the figure as "Based on N cases that reached the plan phase", never as
   * if N were the full population.
   */
  planWorkSampleSize: number

  /**
   * Plan Work spent BUILDING plans, before anyone asked for a change
   * (sc-1212 round 3 — mirrors the API's `SpecialistPersonRow.firstPassMedianMs`).
   *
   * This — not `planWorkMedianMs` — is the number that answers "how fast
   * does this person build a plan". Across the firm **76% of Plan Work time
   * is rework** (measured on production 2026-09-28, see
   * `ChangeRequestRollup` on the API side), so the combined median mostly
   * measures change requests, and a specialist who absorbs a lot of alt
   * scenarios reads as a slow builder on it alone. `null` when no case in
   * the window reached the build phase — not zero.
   *
   * **Not comparable BETWEEN people**, and the in-flight fix above did not
   * change that — a third, separate measurement defect (not fixed this
   * round): this measures how long each person LEAVES a ticket in a stage,
   * not how long they take to build a plan. Production, 30 days: Ryan 41 of
   * 56 cases entered the build phase at a 1.67h median, Jacob 36 of 41 at
   * 69.75h, Garrett 8 of 59 at 8.55h, Joe 0 of 52. Usable against one
   * person's OWN history over time; never for ranking people against each
   * other.
   */
  firstPassMedianMs: number | null
  /** Cases behind `firstPassMedianMs` — same "coverage, not population"
   * caveat as `planWorkSampleSize`. */
  firstPassSampleSize: number

  /**
   * Plan Work spent servicing CHANGE REQUESTS — Revisions and Alt Scenarios
   * (sc-1212 round 3 — mirrors `SpecialistPersonRow.reworkMedianMs`).
   *
   * Its own sample, not a subset of `planWorkSampleSize` by position: a
   * case that took no change request contributes to the first-pass median
   * and not to this one, which is what makes this read as "when a change
   * was asked for, how long did it take" rather than being diluted toward
   * zero by every case that never needed one.
   *
   * `null` means this person took NO change requests in the window — a
   * real and GOOD outcome, not missing data. Never render it the same as an
   * unmeasured value; say so in words (see `SpecialistPeopleTab`).
   */
  reworkMedianMs: number | null
  reworkSampleSize: number

  /**
   * Cases whose first-pass / rework clock is STILL RUNNING — sitting in one
   * of those stages right now, so its duration is time-so-far. Mirrors the
   * API's `SpecialistPersonRow.firstPassInFlight`/`.reworkInFlight`
   * (`apps/api/.../analytics/types/specialist-people.types.ts`).
   *
   * **Excluded from `firstPassMedianMs`/`reworkMedianMs` above and shown
   * BESIDE them, never added in.** `stage_durations_ms` keeps incrementing
   * the entry for the stage a ticket is sitting in right now, so pooling an
   * in-flight case with finished ones measures who is holding the open queue
   * and reports it as how slowly they work — on production this inflated the
   * lead specialist's rework median from a real 6.6d to a pooled 8.1d.
   *
   * **Not a defect count.** A specialist is expected to have some live
   * cases. Render as "N in flight · oldest Xd" in muted text under the
   * median, and render NOTHING extra when the count is 0 — a printed "0 in
   * flight" reads as a claim nothing is happening, not an absence.
   *
   * `*OldestMs` is null exactly when the matching `*InFlight` count is 0.
   */
  firstPassInFlight: number
  firstPassInFlightOldestMs: number | null
  reworkInFlight: number
  reworkInFlightOldestMs: number | null

  /**
   * Plan Delivery, split three ways and attributed to the case's HubSpot
   * OWNER: was the plan ready before the client's first delivery meeting?
   * (`lateApprovalEp`, below, is a fourth, separate count on the same row.)
   *
   * - `onTimeEp` — ready at least one business week (5 business days) before
   *   the meeting. (Was 7 until 2026-09-28.)
   * - `lateEp` — ready, but inside that window. A process miss — **never add
   *   this to `missedEp`**, they are different failures. On production the
   *   first outnumbers the second seven to one.
   * - `missedEp` — the meeting happened with no plan ready. The real failure.
   *
   * Business days are Mon–Fri with no holiday calendar, and any surface that
   * shows these must say so.
   *
   * These answer a different question from `forecastsCreated` and
   * `escapePlansGenerated` beside them, and neither is a subset of the other.
   *
   * Windowed on the MEETING date, not the delivery date: a plan finished
   * weeks before the range still counts when its meeting falls inside it.
   *
   * Counts, so `0` is a measured zero and renders as `0`, never `—`.
   */
  onTimeEp: number
  lateEp: number
  missedEp: number
  /**
   * First meeting still in the future, so no verdict is due yet.
   *
   * **Never add this to `lateEp` or `missedEp`.** Nobody has had the chance
   * to be late yet. Show it as context beside the three real counts, not as
   * a fourth column competing with them.
   */
  pendingEp: number

  /**
   * Cases where the data-gathering form was approved too late for anyone to
   * have delivered — the case did not exist until the day the client was
   * presented to, or later (sc-1212, 2026-09-29). Mirrors the API's
   * `SpecialistPersonRow.lateApprovalEp`
   * (`apps/api/.../analytics/types/specialist-people.types.ts`).
   *
   * **A PLANNER miss, shown on the specialist's row.** This table's roster
   * is SPECIALIST + ANALYST only, and the planner who was actually late has
   * no row here to carry it — dropping the count entirely would hide it from
   * the whole page, so it lands here instead (Tyler, 2026-09-29). It is its
   * own colour (`EP_OUTCOME_TONE.lateApproval`, `info`), never folded into
   * `lateEp` or `missedEp`: the specialist could not have built a plan for a
   * case that had not been opened yet.
   *
   * The records drill-in for this segment names the APPROVING PLANNER, not
   * the ticket owner — see `AnalyticsRecordRow.owner`'s behavior for
   * `metric: 'epOutcomes', outcome: 'lateApproval'`. Cindy Banchy is the
   * worked example: presented at 20:00, form approved by Marc Russell at
   * 20:57, case created 17 seconds after that under a specialist who never
   * had it.
   */
  lateApprovalEp: number

  /**
   * Cases reaching Ready in the window, split by what KIND of work they
   * were (sc-1212 round 5). Mirrors the API's `SpecialistPersonRow.
   * rothologyCases`/`.financialPlanningCases`
   * (`apps/api/.../analytics/types/specialist-people.types.ts`).
   *
   * **The point of the split.** Ryan Boyle and Garrett Leet each closed
   * ~247 Rothology cases over 180 days and looked identical on this table
   * — but Ryan also owned 23 of 26 IE and 20 of 21 Financial Planning
   * cases, roughly 517 plan-work days of load nothing here could see.
   *
   * Financial Planning means IE + FP together (Tyler's copy, 2026-09-30).
   * Annual Review and unclassified cases are counted by neither column —
   * they have their own kinds, and folding them into Rothology would
   * overstate it by 352 cases on production.
   */
  rothologyCases: number
  financialPlanningCases: number

  /**
   * Plan Work spent on Financial Planning cases, as a TOTAL rather than a
   * median (sc-1212 round 5). Mirrors the API's `SpecialistPersonRow.
   * financialPlanningPlanWorkMs`.
   *
   * The question is who is CARRYING more, which is a volume question a
   * median cannot answer. A per-person Financial Planning median would
   * also be undefined for everyone but the one person who owns nearly all
   * of it, so most rows would render an em-dash and the column would look
   * broken.
   *
   * Same population gate as `planWorkMedianMs` — only cases that reached
   * the plan-work phase — so the two reconcile with each other. **A real
   * zero, never null** — unlike `reworkMedianMs`, "no Financial Planning
   * work" is measured, not missing, so it sorts and renders as `0`.
   */
  financialPlanningPlanWorkMs: number

  /** The same COUNTED figures for the prior period. Present only when the
   * caller requested `compare=true` — see `SpecialistPersonPriorPeriod`. */
  previous?: SpecialistPersonPriorPeriod
}

/**
 * A row's COUNTED figures for the period immediately before the window, for
 * a change indicator. Mirrors the API's `SpecialistPersonPriorPeriod`
 * (`apps/api/.../analytics/types/specialist-people.types.ts`) exactly.
 *
 * **Counts only — no medians, deliberately.** A median over a different
 * population is not comparable to this one — the samples are different
 * sizes, and on a five-person team a month's rework median can move two days
 * because one case landed. An arrow beside a median would invent a trend out
 * of sample noise; never render one. Counts are comparable because they are
 * counts of the same kind of event.
 *
 * Present only when `compare=true` was requested — see
 * {@link SpecialistPeople.previousWindow}.
 */
export interface SpecialistPersonPriorPeriod {
  forecastsCreated: number
  escapePlansGenerated: number
  rothologyCases: number
  financialPlanningCases: number
  onTimeEp: number
  lateEp: number
  missedEp: number
  lateApprovalEp: number
}

export interface SpecialistPeople {
  window: AnalyticsWindow
  rows: Array<SpecialistPersonRow>

  /**
   * The window each row's `previous` block was measured over. Absent unless
   * `compare=true` was requested.
   *
   * For `range=month` this is the whole PREVIOUS calendar month; for every
   * other range it is the same span immediately before this window.
   */
  previousWindow?: AnalyticsWindow

  /**
   * True when the window is a calendar month still in progress.
   *
   * **Never render a raw delta when this is set.** On the 8th of the month,
   * eight days of data would be set against a whole prior month and every
   * arrow would point down. Show "month in progress" or compare pace instead
   * — see the API's `isPartialMonth` doc comment
   * (`apps/api/.../analytics/lib/date-buckets.ts`).
   */
  isPartialWindow?: boolean
}

// ---------------------------------------------------------------------------
// Change requests (sc-1212 round 3) — GET /analytics/change-requests
// ---------------------------------------------------------------------------

/**
 * One kind of change request, costed AND flowed. Mirrors the API's
 * `ChangeRequestKindRollup` (`apps/api/.../analytics/types/change-requests.types.ts`)
 * exactly.
 *
 * Alt Scenarios and Revisions (and Additional Info) are kept apart rather
 * than summed because they are different asks with very different shapes:
 * on this endpoint's own window (cases that reached Ready in the 180 days to
 * 2026-09-28), Alt Scenarios ran 300 cases at a 6.76-day median, Revisions
 * 180 at 2.72. Reporting one number would hide that an alt scenario costs
 * roughly 2.5x a revision.
 */
export interface ChangeRequestKindRollup {
  /** Matches a `CHANGE_REQUEST_FLOW_KINDS` key — the "Change requests" tile's
   * drill-in buttons switch on this, not on `label`, to pick which
   * `AnalyticsRecordsMetric` each opens. */
  key: string
  /** The HubSpot stage id, so the UI can drill straight through. */
  stageId: string
  label: string
  /** Cases that entered this stage at all inside the window. */
  cases: number
  /** Every millisecond spent in this stage across those cases. */
  totalMs: number
  /**
   * Median per case that took one — null when no case did.
   *
   * Over the SETTLED cases only, so `medianSampleSize` is below `cases` by
   * `inFlight`. A case still sitting in this stage has a duration that is
   * still climbing, and pooling it with finished ones made this median rise
   * between page loads with no work done. `cases`/`totalMs` above keep every
   * case, live work included — they are volume, and that time has genuinely
   * been spent.
   */
  medianMs: number | null
  medianSampleSize: number
  /** Cases sitting in this stage right now — counted in `cases`/`totalMs`
   * above, withheld from `medianMs`. */
  inFlight: number

  /**
   * Requests RAISED in the window — moves into this stage.
   *
   * Counted per move, not per case: a case sent back twice is two requests,
   * because the second is real additional demand on the specialist.
   */
  raised: number
  /** Requests CLEARED in the window — moves out of this stage, back in
   * flow. */
  cleared: number
  /**
   * Of `raised`, the ones that arrived when the case was already OVER —
   * Closed or Refund Review Sent. Mirrors the API's
   * `ChangeRequestKindRollup.reopened` exactly.
   *
   * **A subset of `raised`, never added to it.** An automation closes a case
   * after the completed escape plan meeting, so these are requests that
   * landed AFTER the final meeting: the case had to be reopened, reworked
   * and closed again. Tyler's question, 2026-10-01, from a case that went
   * Ready → Alt Scenarios → Closed → Alt Scenarios with the last three on
   * the same day.
   *
   * On production: Revisions 76 of 764, Alt Scenarios 35 of 490, Additional
   * Info 31 of 350 — 142 of 1,604 requests, 8.9%. Low, and the expensive
   * kind. Render as a count AND its share of `raised` ("came back after we
   * closed it"), never summed into `raised` — that would double-count.
   */
  reopened: number
  /**
   * `raised - cleared`. Positive means the backlog grew.
   *
   * A flow WITHIN the window, not the size of the open backlog — a request
   * raised before the window and cleared inside it subtracts without ever
   * having been added. Read it as a direction, not a level; never render it
   * as "the backlog is N", only as "the backlog grew/shrank by N".
   */
  netChange: number
}

/**
 * How many arrivals at Ready for Presentation were the case's FIRST.
 * Mirrors the API's `CasesReadySplit` exactly — the Cases Ready tile's own
 * split (sc-1212 round 3).
 *
 * The delivery count on its own answered "how much did we ship" and hid that
 * a large part of it is shipping the same plan again: over the 30 days to
 * 2026-09-29, 293 arrivals split 164 first deliveries against 129
 * re-deliveries — 44% re-delivery.
 *
 * Counted from the transition ledger by arrival ordinal, so it is per
 * ARRIVAL like the Cases Ready tile it splits, not per case — a case
 * re-delivered twice contributes one first delivery and two re-deliveries.
 */
export interface CasesReadySplit {
  total: number
  firstDelivery: number
  reDelivery: number
}

/**
 * Whether change requests arrive in time to be delivered — mirrors the API's
 * `ChangeRequestArrivalTiming` (sc-1212 round 7).
 *
 * Everything else on the Change Requests card measures rework VOLUME and
 * DURATION. This measures its TIMING, which is the bigger problem: over 180
 * days, 272 of the 497 requests that had a delivery meeting ahead of them
 * arrived already past the latest safe start, and 112 arrived when being ready
 * five business days before the meeting was arithmetically impossible.
 *
 * **Read it as a fact about the REQUEST, never about the owner.** A specialist
 * cannot be graded on when a client changes their mind. `bySource` is what
 * makes the number actionable: it separates client-driven from
 * internally-driven requests.
 */
export interface ChangeRequestArrivalTiming {
  arrivals: number
  /**
   * Arrivals with nothing booked ahead — 207 of 704. **Not a failure**: with
   * no meeting there is no date to be late against, so these are excluded from
   * the rates rather than counted as on time.
   */
  noMeetingAhead: number
  /** The denominator for the two counts below. */
  withMeetingAhead: number
  /** Past the latest start that would have made the meeting, on past form. */
  pastSafeStart: number
  /** Past the point where the five-business-day promise was still arithmetically
   * reachable — the harder claim, and it needs no past form to stand up. */
  pastRequiredReady: number
  /**
   * Arrivals that landed after the client's escape plan meeting was HELD
   * (sc-1212 round 8) — 44 of 704 over 180 days, across 33 cases.
   *
   * **The true version of the `reopened` figure shown beside it**, which counts
   * requests whose source stage was Closed and uses that as a documented proxy
   * for the escape plan meeting. Both are shown: they disagree (6% against
   * 8.9%), and the gap is the cost of the proxy.
   *
   * Closed cases are included by construction — the population is ledger
   * arrivals, which is what makes this countable over time.
   */
  afterEscapePlanHeld: number
  bySource: Array<ChangeRequestArrivalSource>
}

export interface ChangeRequestArrivalSource {
  /** HubSpot's `change_source`; null when none was recorded. */
  source: string | null
  arrivals: number
  withMeetingAhead: number
  pastSafeStart: number
  pastRequiredReady: number
}

/**
 * How much of the firm's Plan Work is servicing changes rather than
 * building plans, AND how the change-request book is moving. Mirrors the
 * API's `ChangeRequestRollup` exactly — one endpoint now backs both the
 * "Plan Work" tile (the cost split, `firstPassTotalMs`/`reworkTotalMs`/
 * `reworkSharePct`) and the "Change requests" tile (the flow,
 * `totalRaised`/`totalCleared`/`netChange`) in `KpiMetricSelector`.
 *
 * **The headline this exists to report: rework is most of Plan Work, and the
 * change-request book is growing.** Plan Work had been reported as a single
 * median, which meant the majority of the work the team does was invisible
 * and made an owner who absorbs a lot of alt scenarios look like a slow
 * plan builder. Separately, the grid counted clearances for all three kinds
 * and requests for only one, so Revisions and Additional Info read as pure
 * throughput — both sides together say the book grew by 31 change requests
 * over the 30 days to 2026-09-29 (164 raised against 133 cleared), which no
 * arrangement of the old four tiles could show.
 */
export interface ChangeRequestRollup {
  window: AnalyticsWindow

  /** Cases in the window that reached the plan-work phase at all. */
  casesConsidered: number
  /** Of those, how many took at least one change request. */
  casesWithChangeRequest: number

  /** Total time building plans first time round. */
  firstPassTotalMs: number
  /** Total time servicing changes to plans already delivered. */
  reworkTotalMs: number
  /**
   * The firm's MEDIAN per case for each half (sc-1212 round 3 correction) —
   * what the Cases Ready tile shows as "New work" and "Revisions", mirroring
   * the API's `ChangeRequestRollup.firstPassMedianMs`/`reworkMedianMs`
   * exactly.
   *
   * Medians, not the totals above: the totals answer "where does our time
   * go" (a firm-wide share), these answer "how long does one of these
   * take" — the question a reader scanning a tile actually has.
   *
   * Each has its own coverage gate, so a case that never took a change
   * request contributes to the first-pass median and not the rework one —
   * otherwise every case that needed no rework would drag the rework median
   * to nothing. `firstPassMedianMs: null` means no case in the window
   * reached the build phase at all — genuinely missing data.
   * `reworkMedianMs: null` means no case took a change request in the
   * window — a GOOD outcome, not missing data. Never render the two the
   * same way; see `KpiMetricTile`'s Cases Ready group for the wording.
   */
  firstPassMedianMs: number | null
  firstPassSampleSize: number
  reworkMedianMs: number | null
  reworkSampleSize: number
  /**
   * Cases whose clock for that half is STILL RUNNING — sitting in one of
   * those stages right now. **Excluded from the two medians above, included
   * in the two totals below** — the totals are volume ("where does our time
   * go"), and withholding live work there would understate the load.
   */
  firstPassInFlight: number
  reworkInFlight: number
  /**
   * `reworkTotalMs` as a percentage of all plan work, 0-100, rounded to one
   * decimal. Null when there is no plan work at all in the window rather
   * than 0, which would claim a measured absence of rework.
   */
  reworkSharePct: number | null

  /** Per kind, in the order they should be charted — never summed together,
   * see `ChangeRequestKindRollup`'s doc comment. */
  kinds: Array<ChangeRequestKindRollup>

  /**
   * Raised and cleared summed across every kind, and the direction of
   * travel — the "Change requests" tile's headline.
   *
   * Same flow-not-level caveat as `ChangeRequestKindRollup.netChange`: a
   * positive `netChange` means the book grew inside this window, not that it
   * holds `netChange` open requests. Never phrase it as a backlog SIZE.
   */
  totalRaised: number
  totalCleared: number
  netChange: number

  /** The Cases Ready tile's first-delivery / re-delivery split — see
   * {@link CasesReadySplit}. Windowed on the transition, not on
   * `readyForPresentationAt`, so it matches the Cases Ready tile beside it,
   * which counts arrivals in the window. */
  casesReady: CasesReadySplit

  /** Whether those requests arrived in time to be delivered (sc-1212 round 7). */
  arrivalTiming: ChangeRequestArrivalTiming
}

// ---------------------------------------------------------------------------
// Cases open (sc-1212, Tyler 2026-10-01) — GET /analytics/cases-open
// ---------------------------------------------------------------------------

/** One day of {@link CasesOpenLevel.days}. Mirrors the API's `CasesOpenDay`
 * exactly. */
export interface CasesOpenDay {
  /** `YYYY-MM-DD` in the reporting zone. */
  day: string
  /** Cases open at the end of that day. */
  open: number
}

/**
 * How many Case Design cases are OPEN right now, and how that level has
 * moved. Mirrors the API's `CasesOpenLevel` exactly — see that type's own
 * doc comment (`apps/api/.../types/cases-open.types.ts`) for the full
 * reasoning; the load-bearing points are kept here too since a UI that
 * forgets them will render this honestly-wrong.
 *
 * **A level, not a flow — the only one on the Cases Ready tile.** Every other
 * figure there counts events inside the date window; this counts what is
 * standing open at an instant. `total` therefore does NOT respond to the
 * Range control and must never be shown with a "vs prior period" delta —
 * that comparison is for counted flows, and a level has no window to compare
 * across. `days` is what carries the time dimension.
 *
 * **87% of `total` is close-out hygiene, not workload.** `awaitingCloseOut`
 * is cases that already reached Ready for Presentation and are simply
 * sitting there — delivered, nobody closed the ticket. `inFlight` is the
 * honest work-in-progress number. Showing `total` alone would read as "1,341
 * cases of work" to a five-person team, which is wrong by about 8x — so
 * `awaitingCloseOut` must stay visible next to `total`, not buried behind a
 * tooltip.
 */
export interface CasesOpenLevel {
  /** Open right now — every unarchived Case Design ticket whose current
   * stage is not Closed or Refund Review Sent. Ignores the Range control. */
  total: number
  /** Of `total`, delivered and simply waiting to be closed out. */
  awaitingCloseOut: number
  /** Of `total`, not yet delivered — genuinely being worked or waiting to
   * be. `awaitingCloseOut + inFlight === total` by construction. */
  inFlight: number
  /** Median age in days of the `awaitingCloseOut` pile, from case creation.
   * Null when nothing is awaiting close-out. */
  awaitingCloseOutMedianAgeDays: number | null
  /** The open level at the END of each day in the window — the only part of
   * this payload the Range control actually affects. */
  days: Array<CasesOpenDay>
  /** Echoed so a reader can see which span `days` covers. */
  window: AnalyticsWindow
  timeZone: string
}

// ---------------------------------------------------------------------------
// Specialist time rollup (sc-1212, stage three) — GET /analytics/specialist-time
// ---------------------------------------------------------------------------

/**
 * Mirrors the API's `SpecialistTimeGrouping`
 * (`apps/api/.../analytics/types/specialist-time.types.ts`). Only groupings
 * that map onto a filter the records audit already has — a bar has to be
 * able to open `KpiRecordsDialog` filtered to its own group, and a grouping
 * with no corresponding filter would be a chart nobody could drill into.
 */
export type SpecialistTimeGrouping = 'person' | 'month'

export interface SpecialistTimeGroup {
  /**
   * The value to filter the audit by when this bar/row is clicked — a
   * Rothology user id for `person`, `YYYY-MM` for `month`. For `month` the
   * drill-through uses `window` below rather than deriving bounds from this
   * key.
   */
  key: string
  label: string
  medianMs: number
  /** Tickets behind the median — a median of two is not worth reading. */
  ticketCount: number
  /**
   * The exact bounds this group covers, sent by the API so the client never
   * recomputes a reporting-zone month boundary. Null for `person`, whose key
   * is a user id and needs no window.
   */
  window: AnalyticsWindow | null
}

export interface SpecialistTime {
  groupBy: SpecialistTimeGrouping
  window: AnalyticsWindow
  /** Median across every considered ticket, ungrouped. Null when nothing
   * was considered. */
  overallMedianMs: number | null
  /** Tickets with durations synced, in window — what the medians are
   * actually computed from. */
  ticketsConsidered: number
  /**
   * Tickets in window whose durations have never synced, so they
   * contributed nothing. Surfaced rather than swallowed: a median over a
   * third of the cases is not the median, and the reader has to be able to
   * tell — point at the resync, don't just show a smaller number.
   */
  unsyncedTickets: number
  groups: Array<SpecialistTimeGroup>
}

// ---------------------------------------------------------------------------
// Orphaned tickets (sc-1212) — GET /analytics/orphaned-tickets
// ---------------------------------------------------------------------------

/** One current-stage's share of the unowned backlog. */
export interface OrphanedStageBucket {
  stageId: string
  label: string
  count: number
}

/**
 * Case Design tickets nobody owns — the toggle next to the Person picker on
 * the Specialists tab (sc-1212). Mirrors the API's `OrphanedTickets`
 * (`apps/api/.../analytics/types/orphaned-tickets.types.ts`) exactly.
 *
 * **Deliberately NOT `AnalyticsProductivity`-shaped and not a
 * `KpiMetricSelector` tile.** Every other metric on this tab counts movement
 * inside `filters`' date range and has a `total`/`perDay`/`people[]`/`days[]`
 * shape built for that. This is a STANDING SNAPSHOT — what is unowned right
 * now — with no daily series and no per-person split, so forcing it into
 * `AnalyticsProductivity` would mean inventing a fake `perDay` nobody
 * measured. It renders instead as its own panel
 * (`OrphanedTicketsPanel.tsx`), swapped in for `KpiMetricSelector` when the
 * toggle is on, not a seventh tile beside it.
 *
 * The endpoint takes NO query params at all — not even the shared
 * `AnalyticsQueryDto` filters — because a standing backlog has no window to
 * scope it against. The panel must say so on screen (the date Range control
 * stays visible, per ANALYTICS_DESIGN_STANDARD.md §7's "never hide a
 * control that would otherwise mislead," but is captioned "not applied to
 * this view" rather than disabled or removed).
 */
export interface OrphanedTickets {
  /** Every unowned Case Design ticket, both kinds below. */
  total: number
  /** No HubSpot owner set on the ticket at all — needs assigning. */
  noOwner: number
  /**
   * An owner is set but maps to no active staff user — a departed employee
   * or an unmapped workflow. Reported separately from `noOwner` because the
   * fix differs: this one needs the owner mapping corrected, not a new
   * assignee.
   */
  unmappedOwner: number
  /** Ordered by HubSpot's stage order (a backlog, not a sorted-by-size
   * chart) — only stages that actually hold something. */
  byStage: Array<OrphanedStageBucket>
}

// ---------------------------------------------------------------------------
// Meetings (sc-1212) — GET /analytics/meetings
// ---------------------------------------------------------------------------

/**
 * HubSpot `hs_meeting_outcome`. Kept as a loose string, not a union, because
 * this mirrors an external HubSpot enum the API passes through verbatim
 * (same posture as `changeSource`/`priority` on `AnalyticsRecordRow`) — a
 * value HubSpot adds later must not throw, it must fall through to the
 * `MeetingOutcomeCell` "Not set"-style default treatment.
 */
export type MeetingOutcome = string

/**
 * One meeting, shared by the "what's next" list and the stage-filtered
 * records dialog — see `MeetingDateCell`/`MeetingTypeCell`/
 * `MeetingOutcomeCell`/`MeetingClientCell` (`-components/`), the reusable
 * cells both surfaces render this row through.
 */
export interface UpcomingMeeting {
  hubspotId: string
  title: string | null
  /** HubSpot `hs_activity_type` — "Data Gathering", "Presentation",
   * "Consultation", etc. */
  meetingType: string | null
  startTime: string
  /** SCHEDULED | COMPLETED | RESCHEDULED | NO_SHOW | CANCELED | DECLINED,
   * or `null` when HubSpot has not recorded one yet. */
  outcome: MeetingOutcome | null
  /** `null` when the meeting's HubSpot contact matches no client — a real,
   * honest outcome (§8's zero-vs-no-data rule), never an error. */
  client: { id: string; name: string } | null
  /** The client's Case Design stage. Both this and `stageLabel` are `null`
   * when the client has no Case Design ticket — also real, not an error. */
  stageId: string | null
  stageLabel: string | null
}

/** Meetings starting in the selected range, counted against the Case Design
 * stage of the client's ticket. `stageId: 'no-case'` reads "No case" — see
 * the API's `UNKNOWN_STAGE`. */
export interface MeetingStageBucket {
  stageId: string
  label: string
  count: number
}

/**
 * Mirrors the API's `AnalyticsMeetings`
 * (`apps/api/.../analytics/types/meetings.types.ts`) exactly.
 *
 * **`nextMeeting`/`upcomingTotal` are deliberately NOT scoped to `window`.**
 * Every range preset on this page looks backwards (7d/30d/90d/QTD/YTD/365d/
 * custom), so "upcoming meetings in the last 30 days" would be empty by
 * construction. `byStage` answers "how were meetings distributed across case
 * stages in this period" (windowed); the upcoming half answers "what is
 * coming up next" (always from now, unwindowed) — two different questions
 * that happen to share a card. `MeetingsPanel` must say so on screen, the
 * same "never let a reader assume a control applies where it doesn't"
 * treatment `OrphanedTicketsPanel` already uses for its own unwindowed view
 * (§11 in ANALYTICS_DESIGN_STANDARD.md) — never let a reader assume the
 * Range control above scopes both halves of this card.
 *
 * The full upcoming list no longer travels on this payload (sc-1212
 * follow-up) — it moved to its own paged endpoint,
 * `GET /analytics/meetings/upcoming` (see {@link AnalyticsUpcomingMeetings}),
 * opened from the card header's icon button rather than rendered inline.
 * This payload keeps only what the card shows without that dialog: the
 * soonest meeting, and the total count for the button's label.
 */
export interface AnalyticsMeetings {
  window: AnalyticsWindow
  /** Ordered like `byStage` reads it off the API: HubSpot pipeline order,
   * "No case" last — never re-sorted by count. */
  byStage: Array<MeetingStageBucket>
  /** The chart's own denominator — sums exactly to the total of `byStage`. */
  totalInWindow: number
  /** The single soonest upcoming meeting from right now, or `null` when
   * nothing is scheduled — the card's inline highlighted line. Never
   * re-derived client-side from the full list; the full list isn't on this
   * payload any more. */
  nextMeeting: UpcomingMeeting | null
  /** Count behind the card header's "Upcoming · N" button — the full
   * upcoming list's total, not just `nextMeeting`'s presence. */
  upcomingTotal: number
}

/**
 * The rows behind one `byStage` bar — `MeetingRecordsDialog`'s "audit the
 * number" job, the same one `KpiRecordsDialog` does for ticket KPIs (§9 in
 * ANALYTICS_DESIGN_STANDARD.md). Paged like `AnalyticsRecords` (§11c) rather
 * than hard-capped.
 *
 * **NOT YET BUILT SERVER-SIDE as of this type shipping** — `GET
 */
export interface AnalyticsMeetingRecords {
  window: AnalyticsWindow
  stageId: string
  stageLabel: string
  /** Total matching rows across every page. */
  total: number
  truncated: boolean
  page: number
  pageSize: number
  pageCount: number
  rows: Array<UpcomingMeeting>
}

/**
 * The full upcoming-meetings list (sc-1212 follow-up) — behind the card
 * header's icon button, `UpcomingMeetingsDialog`. Mirrors the API's
 * `AnalyticsUpcomingMeetings` (`GET /analytics/meetings/upcoming`,
 * `apps/api/.../analytics/types/meetings.types.ts`) exactly.
 *
 * **Deliberately ignores the page's date Range, same as `nextMeeting` on
 * {@link AnalyticsMeetings} above** — "upcoming" always means from now. The
 * endpoint takes `page`/`pageSize`/`experience`/`horizonDays` only; it does
 * not take the full shared filter set (no `range`/`from`/`to`/`department`/
 * `person`).
 *
 * `horizonDays` is a request param, not a response field: how many days
 * FORWARD from right now to include (1–3650), omitted entirely for "all
 * upcoming, no cutoff." It is unrelated to the page's `range` — the dialog
 * carries its own forward-looking control for it (`UpcomingMeetingsDialog`),
 * separate from the Range filter above the card, the same "never let a
 * reader assume a control applies where it doesn't" treatment the card
 * already uses for this half (§11 in ANALYTICS_DESIGN_STANDARD.md) and
 * `OrphanedTicketsPanel` uses for its own unwindowed view.
 *
 * `rows` are already sorted soonest-first by the server — never re-sort
 * client-side.
 */
export interface AnalyticsUpcomingMeetings {
  total: number
  page: number
  pageSize: number
  pageCount: number
  rows: Array<UpcomingMeeting>
}

/** `UpcomingMeetingsDialog`'s own forward-looking window control — see
 * {@link AnalyticsUpcomingMeetings}'s doc comment for why this is separate
 * from the page's `AnalyticsRange`. `'all'` sends no `horizonDays` at all. */
export type UpcomingMeetingsHorizon = 7 | 30 | 90 | 'all'

/**
 * Plan Delivery outcomes — `GET /analytics/ep-outcomes` (sc-1212).
 *
 * The first metric on this page that measures a COMMITMENT rather than
 * throughput: every other number says how much work moved, this one says
 * whether the plan existed before the client sat down.
 *
 * Re-anchored (2026-09-25) from the client's Escape Plan Meeting onto their
 * FIRST delivery meeting (usually the Rothology Presentation, then a
 * follow-up, then the escape plan) — the deadline is still one business week
 * of lead. Split three ways rather than pass/fail: on production, of 65 held
 * meetings, 2 were on time, 55 late and 8 missed — 57 of 65 DID have a plan
 * ready, so a pass/fail read ("2 successful, 63 failed") would tell a
 * manager nothing. `missed` is the alarming outcome; `late` is a process
 * signal, not a client-facing failure.
 *
 * **A fourth outcome, `lateApproval` (sc-1212, 2026-09-29), sits beside
 * these three rather than inside them.** It means the case did not exist
 * until the day of the presentation or later — nobody approved the
 * data-gathering form (the act that creates the Case Design ticket) until
 * the client was already in the room. It is a PLANNER miss, never a
 * specialist one, so it is never summed into `measured`, `late`, or
 * `missed` — see {@link EpOutcome}'s doc comment for the full rule and the
 * Cindy Banchy worked example.
 *
 * **Windowed on the MEETING date, not the delivery date.** A plan finished
 * weeks before the selected range still counts when the meeting it was for
 * falls inside it. The panel must say so — a reader who assumes the Range
 * control filters deliveries will read every number against the wrong event.
 *
 * Mirrors the API's `EpOutcomes`
 * (`apps/api/.../analytics/types/escape-plan-outcomes.types.ts`) exactly.
 */
export interface EpOutcomes {
  window: AnalyticsWindow
  /** Clients whose first meeting has already happened — `onTime + late + missed`.
   * `lateApproval` is deliberately NOT part of this sum — see its own field. */
  measured: number
  onTime: number
  late: number
  missed: number
  /**
   * The form was approved too late for anyone to have delivered — see
   * {@link EpOutcome}'s `lateApproval` doc comment for the full rule and the
   * Cindy Banchy worked example.
   *
   * Reported beside the delivery verdicts, never summed into `measured` or
   * `missed`: nobody could build a plan for a case that did not exist yet, so
   * this case is not gradeable on delivery at all — it's a PLANNER miss, not
   * a specialist one.
   */
  lateApproval: number
  /**
   * First meeting still upcoming, so no verdict is due.
   *
   * **Never folded into `late` or `missed`, on screen or in a total.** Nobody
   * has had the chance to be late yet, and counting it against a specialist
   * would be a false accusation.
   */
  pending: number
  /**
   * Clients with a qualifying meeting but no Case Design ticket.
   *
   * A broken association, not a missed deadline — and with no ticket there is
   * no owner to attribute it to. Surfaced so the panel's total reconciles
   * with what a reader can count elsewhere, never counted as a failure.
   */
  unlinked: number
  /**
   * Median business days of lead across measured clients. Negative values
   * (delivered after the meeting) are included — dropping them would flatter
   * the figure by excluding exactly the cases that went worst.
   */
  medianLeadBusinessDays: number | null
  /** Chronological, bucketed on the MEETING date. Never sorted by size. */
  byMonth: Array<{
    /** `YYYY-MM` in the reporting zone. */
    month: string
    label: string
    onTime: number
    late: number
    missed: number
    /** See `lateApproval` above — never summed into `missed` here either. */
    lateApproval: number
  }>
  /** Worst first (most `missed`, then most `late`) — the list exists to find
   * who needs help. */
  people: Array<{
    userId: string
    name: string
    onTime: number
    late: number
    missed: number
    /** See `lateApproval` above — never summed into `missed` here either. */
    lateApproval: number
  }>
}

// ---------------------------------------------------------------------------
// Forecasts view (sc-1212) — GET /analytics/forecast-queue
// ---------------------------------------------------------------------------

/**
 * Mirrors the API's `ForecastQueueBand`
 * (`apps/api/.../analytics/types/forecast-queue.types.ts`) exactly.
 *
 * **Independent of {@link ForecastQueueGroup} (sc-1212 round 2) — bands
 * answer "when must this start", groups answer "what was promised", and a
 * row's band does NOT determine which of the three section arrays it lands
 * in.** `AT_RISK`/`START_NOW`/`SCHEDULED` require a booked meeting
 * (`deadlineAt`) and so only ever appear on {@link
 * AnalyticsForecastQueue.urgent} or {@link AnalyticsForecastQueue.scheduled}
 * — never {@link AnalyticsForecastQueue.unscheduled}, whose rows have no
 * meeting at all. `UNSCHEDULED` (no meeting) can appear in any of the three:
 * a case with no meeting still has a `requiredReadyBy` from the
 * data-gathering budget clock, and that alone decides whether it's urgent,
 * scheduled or undated. This is what makes the band rollup cards a filter
 * that cuts across all three sections rather than a fourth section of their
 * own — a click narrows each section to one band, it never hides a whole
 * section.
 */
export type ForecastQueueBand =
  | 'AT_RISK'
  | 'START_NOW'
  | 'SCHEDULED'
  | 'UNSCHEDULED'

/**
 * Which of the page's three sections a case belongs in (sc-1212 round 2).
 * Mirrors the API's `ForecastQueueGroup`
 * (`apps/api/.../analytics/lib/forecast-queue-bands.ts`) exactly.
 *
 * **Grouped by what was PROMISED, never by how unusual a delay looks against
 * our own history** — see the API's doc comment on `queueGroupFor` for why a
 * band-shaped grouping was tried and rejected.
 *
 * - `URGENT` — `requiredReadyBy` has passed and the case is still open. 22 of
 *   172 on production. Only ever appears on
 *   {@link AnalyticsForecastQueue.urgent}.
 * - `SCHEDULED` — a `requiredReadyBy` exists (from a booked meeting OR the
 *   data-gathering budget clock) and is still ahead. 56. Only ever appears on
 *   {@link AnalyticsForecastQueue.scheduled}.
 * - `UNDATED` — no meeting AND no data-gathering approval, so nothing has
 *   promised this case anything. 94, of which 81 have genuinely never had a
 *   form approved. Only ever appears on
 *   {@link AnalyticsForecastQueue.unscheduled} (the field keeps its original
 *   name; the group it holds is `UNDATED`).
 *
 * The three arrays are already partitioned by the server on this field; never
 * re-bucket a row by its own `group` client-side, same rule `band` already
 * follows.
 */
/**
 * Which promise produced a row's `requiredReadyBy`. Mirrors the API's
 * `RequiredReadySource` (`apps/api/.../analytics/lib/forecast-queue-bands.ts`).
 *
 * `MEETING` — a booked delivery meeting, less the delivery lead: a client will
 * be sitting down on this date. `BUDGET` — nothing booked, so the firm's own
 * turnaround budget. Both are real dates; only one of them anyone outside the
 * firm knows about, which is why the page says which.
 */
export type RequiredReadySource = 'MEETING' | 'BUDGET'

export type ForecastQueueGroup =
  | 'URGENT'
  | 'SCHEDULED'
  | 'UNDATED'
  /**
   * A manager parked this row — see {@link CaseMute}. REPLACES the group the
   * case would otherwise be in rather than sitting beside it, so a parked case
   * is never counted twice: the urgent count exists to be acted on, and a row
   * already judged un-actionable is not part of it.
   */
  | 'PARKED'

/** Which kind of work a {@link WorkEstimate} measures. */
export type WorkEpisodeKind = 'BUILD' | 'REWORK'

/**
 * How long one piece of delivery work typically takes — the figure
 * `ForecastQueueRow.safeStartAt` and `projectedReadyAt` are built from
 * (business logic owned entirely by the API; sc-1212 round 24). Surfaced so a
 * reader can judge how much to trust a date rather than take it as ground
 * truth.
 *
 * **This is a per-EPISODE measure, not a per-stage one, and that is why it
 * replaced `StageOffset`.** Ready for Presentation is reached repeatedly (the
 * presentation, then again after revisions for the follow-up, then again for
 * the escape plan), so the old "arrival at this stage to the next Ready" span
 * crossed meetings and client revision requests: a 390-day maximum, and 28 of
 * 52 safe-start dates landing before the ticket existed. One episode runs from
 * the previous Ready (or the DG approval, for the first) to the next Ready.
 * `BUILD` is the first plan; `REWORK` is every later one. They are different
 * work, so they carry different numbers.
 *
 * **`typicalDays` is a p50: a typical case, not a safe one.** Half of
 * episodes run longer. A date built on it means "a normal case would still
 * make it", never "this will make it", so the UI states that and shows
 * `spreadDays` (p75) beside it instead of implying precision the number lacks.
 * Calendar days, unlike the business-day delivery lead.
 *
 * **Sample size is part of the contract:** an estimate backed by 20
 * observations is not the same promise as one backed by 339, and the UI says
 * so. Per-type rows only exist above an API-side sample floor; every other
 * type resolves to the `serviceType: null` firm-wide row, which the UI labels
 * as a fallback.
 */
export interface WorkEstimate {
  /** The `service_type` token; `null` is the firm-wide fallback row. */
  serviceType: string | null
  kind: WorkEpisodeKind
  /** p50 days — the basis of the date. */
  typicalDays: number
  /** p75 days — shown beside `typicalDays` for the spread, never a date. */
  spreadDays: number
  sampleSize: number
}

/**
 * One forecast on a queued case, for the QA drill-in (sc-1212 round 2).
 * Mirrors the API's `ForecastQueueForecast`
 * (`apps/api/.../analytics/types/forecast-queue.types.ts`) — a summary, not
 * the forecast itself: enough to tell one from another and decide whether to
 * open it.
 */
export interface ForecastQueueForecast {
  id: string
  title: string
  status: string
  createdAt: string
  createdByName: string | null
  /** The client's one active escape plan, if this is it. */
  isActiveEscapePlan: boolean
  /**
   * Raised AFTER this case was opened — the same test `ForecastQueueRow.
   * workStarted` uses. The distinction is the point of the drill-in: a
   * returning client carries forecasts from a previous case, and those still
   * belong in the list even though they didn't start this case's clock. On
   * production 133 of 170 queued clients have forecasts but none raised
   * since the case opened.
   */
  raisedAfterCaseOpened: boolean
}

/**
 * One case in the Forecasts queue — a client whose Case Design work implies
 * a forecast/Escape Plan is owed, whether or not a meeting has been booked
 * for it yet. Mirrors the API's `ForecastQueueRow`
 * (`apps/api/.../analytics/types/forecast-queue.types.ts`) exactly.
 */
/**
 * The deadline meeting's own columns, mirroring the API's
 * `ForecastQueueDeadlineMeeting` field-for-field.
 *
 * `hostName` is the meeting's host, NOT the queue row's `owner` — that is the
 * ticket's owner, and the two differ more often than not.
 */
/**
 * New plan, or a change to one already delivered.
 *
 * Not a fourth section: the three groups answer WHEN a case is due, this
 * answers WHAT KIND of work it is. Rendering change requests as their own
 * section would pull the ones with a booked meeting out of Urgent and hide
 * exactly the rows that matter. Badge and filter instead.
 */
export type ForecastQueueCaseKind = 'NEW_PLAN' | 'CHANGE_REQUEST'

export interface ForecastQueueDeadlineMeeting {
  hubspotMeetingId: string
  title: string | null
  meetingType: string | null
  startTime: string | null
  endTime: string | null
  outcome: string | null
  source: string | null
  location: string | null
  videoUrl: string | null
  hostName: string | null
  contactIds: Array<string>
  lastModifiedAt: string | null
}

export interface ForecastQueueRow {
  ticketId: string
  subject: string | null
  hubspotUrl: string | null
  client: { id: string; name: string } | null
  stageId: string
  stageLabel: string
  /**
   * `id` is the HUBSPOT owner id; `userId` is the application user it maps to.
   *
   * The two are not interchangeable. `UserPicker` emits user ids, so the
   * client-side owner filter MUST compare `userId` — comparing `id` matches
   * nothing, silently. `userId` is null for a ticket whose owner maps to no
   * active staff member ("Unmapped owner" rows).
   */
  owner: { id: string; userId: string | null; name: string } | null
  /** The soonest upcoming Escape Plan Meeting. Null on every row in
   * {@link AnalyticsForecastQueue.unscheduled} — never a guess. */
  deadlineAt: string | null
  deadlineType: string | null
  /**
   * The deadline meeting itself, for the shared `HubspotMeetingHoverCard` —
   * a date alone never said WHICH meeting it was (Tyler, sc-1212 round 3).
   *
   * Flat, and missing attendees/clients/issues on purpose: the hover card
   * hides empty sections, and loading those per row would cost joins this
   * ~175-row snapshot does not need. `ForecastQueueSection` maps it onto
   * `HubspotMeetingRecord` the same way `MeetingRecordingsList` and
   * `EscapePlanMeetingsAuditList` already map their own flat shapes.
   *
   * Null exactly when `deadlineAt` is.
   */
  deadlineMeeting: ForecastQueueDeadlineMeeting | null
  /** Negative once the deadline has passed. Null when there is no
   * `deadlineAt` to measure against. */
  daysToDeadline: number | null
  /**
   * `requiredReadyBy` minus the typical (p50) length of this kind of work
   * (see {@link WorkEstimate}) — the latest date a typical case can still
   * start and make it. Null whenever `offsetDays` is null (no estimate to
   * trust) — render "—", never a guessed date.
   *
   * **Floored at the earliest the work could have begun**, so it can never name
   * a date from before the case existed. It used to: Dana Fantell's DG form was
   * approved 2026-10-15 and her Presentation booked the same day for Oct 30,
   * yet the column read "Oct 4". When the floor had to move the date,
   * {@link ForecastQueueRow.shortTurnaround} says so.
   */
  safeStartAt: string | null
  /**
   * The unfloored safe start fell before work could begin: the meeting was
   * booked with less runway than this kind of work historically takes, so the
   * case could not have met the date whoever it was assigned to.
   *
   * **A booking outcome, not a specialist's delay** — it routes the row to
   * whoever schedules meetings, and must not be read as a mark against the
   * owner. **Not rare:** measured on production 2026-10-20, 19 of the 55 open
   * Urgent + Scheduled rows carry it, with a median runway of 14 business days
   * against the firm's 34-business-day delivery target — and across 718 cases
   * since May the median runway has been 17 business days every month, so this
   * is the firm's normal scheduling cadence rather than a run of exceptions.
   * Design for the common case.
   *
   * **Read it as possibly absent even though it is typed required.**
   * `apps/web` and `apps/api` deploy separately (the same hazard
   * `postDeliveryWork` documents; a React #130 white-screen on 2026-10-07 came
   * from exactly this), so an older API omits it. Treat absent as `false` —
   * `shortTurnaroundReading` does that for you.
   */
  shortTurnaround: boolean
  /**
   * Business days from the earliest possible start to the delivery meeting —
   * the runway the case actually got, to read against
   * {@link AnalyticsForecastQueue.budgetBusinessDays}. Null when either end is
   * unknown.
   *
   * **Read it as possibly absent** (older API): treat absent as `null`, never
   * as 0 — "0 business days" would claim a same-day booking. Use
   * `shortTurnaroundReading`.
   */
  turnaroundBusinessDays: number | null
  offsetDays: number | null
  offsetSampleSize: number | null

  /** When the case entered the stage it is in now — the anchor `projectedReadyAt`
   * is computed from. Not rendered directly; carried so a reader/InfoHint can
   * explain the projection's basis. */
  stageEnteredAt: string | null
  /**
   * When this case is expected to reach Ready, on past form —
   * `stageEnteredAt` plus the typical (p50) days for this kind of work. Answers
   * a different question from `safeStartAt`: that one is the latest start, this
   * one is what's likely.
   *
   * **Fixed at the moment the case entered its current stage.** It does not
   * drift while the case sits there — it only re-projects when the case
   * moves to a new stage. Never render this as a live countdown.
   *
   * Null when the stage has too few samples to publish a median offset —
   * render "—", never a guess. Same rule `safeStartAt` already follows.
   */
  projectedReadyAt: string | null
  /**
   * The projected date has gone by and the case is still open: the forecast
   * failed.
   *
   * **Read it with `slackDays`, which flatters the row on its own.** Slack
   * measures the earliest the case could STILL be ready, i.e. it assumes the
   * case finishes the moment you look at the page — so an expired estimate
   * rendered "on track — no spare days" on a row whose forecast had passed
   * three weeks earlier with one stage transition to its name. `formatSlackDays`
   * and `projectedReadyTone` both take this flag; neither may be called
   * without it.
   *
   * The date itself is NOT re-based onto today — see the API's
   * `resolveProjection` for why re-forecasting needs a start signal production
   * does not have (`workStarted` covers 95% of Rothology cases but 15 of 44 IE
   * ones). 17 of 154 open rows on 2026-10-20.
   *
   * **Optional: the halves deploy separately.** Absent reads as not lapsed,
   * which is the wording the page had before the field existed.
   */
  projectionLapsed?: boolean
  /**
   * When this case NEEDS to be Ready: the booked meeting minus one business
   * week, or — with no meeting — the turnaround budget (published as
   * `AnalyticsForecastQueue.budgetBusinessDays`) less that same week, counted
   * from the DG approval that started the budget clock.
   *
   * **Null means neither exists** — no meeting is booked and the DG form
   * hasn't been approved. That is "nothing has promised this case a date
   * yet," not "late." Must never render as overdue.
   */
  requiredReadyBy: string | null
  /**
   * Which promise produced `requiredReadyBy`: a booked delivery `MEETING`, or
   * the firm's own turnaround `BUDGET` when nothing is booked.
   *
   * `requiredReadyBy` is not rendered as a column — it only decides the
   * section — so a budget-dated row appeared under Scheduled, said "No meeting
   * booked", and showed nothing to say what it was scheduled against.
   * `budgetDueLine` uses this to add that line, and only for `BUDGET`.
   *
   * **Optional for the separate-deploy window**; absent means say nothing.
   */
  requiredReadyBySource?: RequiredReadySource | null
  /** `requiredReadyBy` minus `projectedReadyAt`, in days. Negative means the
   * case is projected to miss its required date; null whenever either side
   * is null. */
  slackDays: number | null
  /**
   * The OWNER's own median Plan Work duration (ms), for context beside the
   * owner's name — NEVER an input to `projectedReadyAt`, which is
   * deliberately firm-wide. A per-owner projection would go green the moment
   * an at-risk case is reassigned to a faster colleague, exactly the action
   * an at-risk flag is supposed to prompt.
   */
  ownerPlanWorkMedianMs: number | null
  /** Cases behind `ownerPlanWorkMedianMs` — a median of one or three is not a
   * median; the UI suppresses the rate below a floor for this reason. */
  ownerPlanWorkSampleSize: number

  band: ForecastQueueBand
  /**
   * Which of the page's three sections this case belongs in — see
   * {@link ForecastQueueGroup}. Grouped by what was promised, never by how
   * unusual the delay is against our own history. Independent of `band`:
   * a row's group decides which section it renders in, its band decides the
   * `BandBadge`/rollup-card filter — the two answer different questions and
   * a card click narrows all three sections by `band`, never by `group`.
   */
  group: ForecastQueueGroup
  /**
   * The live park on this case, non-null exactly when `group` is PARKED, so
   * the parked list can explain itself without a second request.
   */
  mute: CaseMute | null

  /**
   * `CHANGE_REQUEST` once the case has ever reached Ready for Presentation —
   * the plan was built and delivered, and it is open again because someone
   * asked for something different.
   *
   * On production 2026-09-28, 26 of 170 open cases. They used to be
   * indistinguishable from new plans, and 13 of them sat in Urgent purely
   * because the case-open budget had expired months ago.
   */
  caseKind: ForecastQueueCaseKind

  /**
   * What KIND of work this case is — Rothology, Financial Planning (IE +
   * FP), Annual Review, or unclassified (sc-1212 round 5). Mirrors the
   * API's `ForecastQueueRow.caseWorkKind`
   * (`apps/api/.../analytics/types/forecast-queue.types.ts`) exactly.
   *
   * **A different question from `caseKind`, and both must stay.** `caseKind`
   * asks whether the case was ever delivered (history); `caseWorkKind` asks
   * what SORT of work it is (classification). A change request on an IE
   * case is `CHANGE_REQUEST` **and** `FINANCIAL_PLANNING`.
   *
   * 14 of the 170 open cases are Financial Planning today, and they were
   * indistinguishable from Rothology work before this existed.
   *
   * `ANNUAL_REVIEW` here always agrees with `isAnnualReview` by
   * construction — see `CASE_WORK_KINDS`, where Annual Review takes
   * precedence precisely so the queue's two Annual Review controls cannot
   * disagree.
   */
  caseWorkKind: CaseWorkKind

  /**
   * The ticket has no `service_type` — see
   * `AwaitingNextMeetingRow.serviceTypeMissing` for the count and why it is a
   * different question from `caseWorkKind === 'UNCLASSIFIED'`.
   */
  serviceTypeMissing: boolean

  /** When the change currently open was asked for; null when none is. */
  changeRequestedAt: string | null

  /**
   * Whole days since `changeRequestedAt`.
   *
   * **Show this instead of `budgetBusinessDaysLeft` on a change request.**
   * That budget covers BUILDING a plan, it was spent on the plan that already
   * shipped, and on a 76–122-day-old case it always reads as blown.
   */
  changeRequestAgeDays: number | null
  /**
   * No data-gathering approval exists at all — the specialist cannot start
   * however close the meeting is. 68 of 169 open cases on production (down
   * from the ~135 the badge used to claim, before `budgetStartUnknown` split
   * out the cases that WERE approved but can't be dated).
   *
   * **A flag on the row, never a section.** Some of these 68 have a booked
   * client meeting and no approval — the most dangerous cases in the book —
   * so grouping by this instead of by `group` was tried and rejected: it
   * would file those cases out of Urgent/Scheduled and hide them. Render it
   * as a badge beside the row so a reader can see the case is real and the
   * blocker is upstream from the specialist.
   */
  waitingOnDataGathering: boolean
  /**
   * The data-gathering form WAS approved, but we cannot place when — the
   * turnaround timer has no reliable start date.
   *
   * Its own flag, never folded into `waitingOnDataGathering`, because it must
   * not read as either neighboring claim: the case is not waiting on the
   * client (an approval exists), and the timer is not simply unstarted. It
   * happens when the only approval on record is an activity-log backfill with
   * no `approved_at` to date it. Mirrors the API's `ForecastQueueRow.
   * budgetStartUnknown` (`apps/api/.../analytics/types/forecast-queue.types.ts`).
   */
  budgetStartUnknown: boolean
  /**
   * The budget counts from the day the ticket was opened, not from a
   * data-gathering approval — a **second-service case**.
   *
   * An IE Pillars or SO ticket is raised by hand against data gathering the
   * firm approved for the client's Rothology case, a median 62 days earlier.
   * That approval is stored and correct; it is just not this work's start,
   * because Pillars work comes after Rothology (Tyler, 2026-10-20). So the
   * countdown on these rows is anchored differently from every other row's,
   * and the tooltip has to say so — 26 open tickets on production.
   *
   * **Optional: the API and web halves deploy separately** even from one
   * merge, so a bundle can run against an API that predates the field. Absent
   * means "do not claim anything about the anchor", never `false`. Mirrors the
   * API's `ForecastQueueRow.budgetStartFromTicketCreation`
   * (`apps/api/.../analytics/types/forecast-queue.types.ts`).
   */
  budgetStartFromTicketCreation?: boolean
  /**
   * The experiences this case's `service_type` names, resolved server-side
   * (sc-1212 round 2) so the page can filter by experience entirely in the
   * browser rather than round-tripping to the API on every change. Empty is
   * normal — 249 production Case Design tickets carry no service type at
   * all — and Annual Review is its own flag (`isAnnualReview`) below, never
   * one of these. Mirrors the API's `ForecastQueueRow.experiences`
   * (`apps/api/.../analytics/types/forecast-queue.types.ts`).
   */
  experiences: Array<RollupExperienceType>
  ageDays: number | null
  /**
   * Business days left of the turnaround budget, negative once overspent. The
   * budget's length is the firm's delivery target, published beside the rows as
   * `AnalyticsForecastQueue.budgetBusinessDays` and stored in
   * `JourneySchedulingSetting.deliveryTargetBusinessDays` — read it there,
   * never restate it here. (History: 25 until 2026-10-07, 34 from then.) The
   * firm promises specialist AND planner work both finish within it, counted
   * from the data-gathering form being FIRST APPROVED — not of the case opening. On production a case waits
   * an average of 26.2 days between the two, so counting from the ticket would
   * spend the whole budget before anyone could legitimately start.
   *
   * **Never clamp this at zero.** A case 11 days over budget is a different
   * conversation from one that just ran out, and flooring both to 0 would make
   * them read identically.
   *
   * **A different clock from `band`/`safeStartAt`, deliberately.** Those
   * answer "when must this start to make its meeting"; this answers "how much
   * of the promised turnaround is left." A case with no meeting booked has no
   * band and still has a budget — and that is most of the queue, so this must
   * render in both the scheduled and unscheduled sections.
   *
   * **`null` covers two different causes — check `budgetStartUnknown` to tell
   * them apart before rendering.** Either the DG form has not been approved
   * yet (`waitingOnDataGathering`, the common case), or it WAS approved but
   * the approval date is unrecoverable (`budgetStartUnknown`). Never "unknown"
   * as a single bucket, not zero, and never "25 left" — but the two causes get
   * different words ("Timer not started" vs. "Approved — start date
   * unknown"), because the second must not read as "waiting on the client".
   */
  budgetBusinessDaysLeft: number | null

  /**
   * When the timer started: the case's FIRST data-gathering approval, or null
   * when it has not been approved.
   *
   * Read from the DG activity log, never from `approved_at` — that column
   * holds the LATEST approval and is nulled on a revert, so on production 249
   * approved forms carry no timestamp at all and 114 disagree with their first
   * approval, one by 105 days.
   */
  budgetStartedAt: string | null
  /** True once a forecast exists for this case — raised any time after the
   * case opened, not gated to the Escape Plan Meeting. `false` is the signal
   * this page exists to surface: nobody has started yet. */
  workStarted: boolean
  /**
   * Every forecast for this case's client, newest first (sc-1212 round 2) —
   * the QA drill-in this page exists to support. Carried on the row rather
   * than fetched when the dialog opens: the page is a single snapshot, and
   * loading every client's forecasts up front costs less than a round trip
   * per click. See `workStarted` and `ForecastQueueForecast.
   * raisedAfterCaseOpened` for why a row can read "No forecast yet" and
   * still have entries here — most do.
   */
  forecasts: Array<ForecastQueueForecast>
  /**
   * An Annual Review rather than new business. ARs are second priority by
   * standing rule and none of them carries an Escape Plan Meeting, so every
   * one lands in the unscheduled section by construction.
   */
  isAnnualReview: boolean
  /**
   * An AR ticket left over from the process the in-app Annual Review feature
   * replaced — nothing in `annual_reviews` refers to it. 80 of these are open
   * at a median age of 86 days. Residue to be closed, not work in queue.
   */
  isLegacyAnnualReview: boolean

  /**
   * Show this case to the manager again (sc-1212 manager tools). Mirrors the
   * API's `ForecastQueueRow.needsAttention` exactly
   * (`apps/api/.../analytics/types/forecast-queue.types.ts`).
   *
   * **A different question from `band`.** The band says what is at risk;
   * this says what is at risk AND has been sitting there with nobody
   * looking — the difference between a list you scan and a list you act on.
   *
   * True when the case has been unchanged for the stale threshold AND
   * nobody has audited it for the audit-quiet threshold. Both, never
   * either — stale alone would re-raise a case the morning after it was
   * chased, which teaches a manager that auditing achieves nothing.
   *
   * **Always `false` with no snapshot history** (`unchangedDays === null`)
   * — the capture job has not covered this case yet, which is not the same
   * as something being wrong. Never render this as if it were.
   */
  needsAttention: boolean

  /**
   * Whole days this case has sat unchanged — band, stage and whether work
   * started, NOT the computed dates, which drift on their own as firm-wide
   * medians move.
   *
   * **`null` means the snapshot job has not covered this case yet — render
   * nothing, or a neutral "—", never as if something were wrong.** The job
   * only starts collecting from the day it ships.
   */
  unchangedDays: number | null

  /**
   * Whole days since a manager last audited or chased this case. `null`
   * means nobody ever has — not a state to render as alarming on its own,
   * only in combination with `needsAttention`.
   */
  daysSinceAudit: number | null

  /**
   * The most recent pass, with the manager's own words — mirrors the API's
   * `CaseLastAudit` (sc-1212 round 7).
   *
   * `daysSinceAudit` colours the marker on every row; this is only read on
   * hover, and is what makes the marker worth hovering (Tyler: "we dont get
   * to hover and read the audit note from the record"). `null` when nobody
   * has ever audited the case; `note` is independently null when the pass was
   * recorded without words, which is a normal thing to do.
   */
  lastAudit: CaseLastAudit | null

  /**
   * WHY this case has no delivery meeting booked — `null` when it has one
   * (sc-1212 round 7). Mirrors the API's `UnscheduledReason`.
   *
   * The `UNSCHEDULED` band was one lane labelled "No meeting booked", and
   * nothing could be done with it because its 84 rows are three different
   * situations: 79 that correctly have nothing booked yet (64 of them still in
   * DG Incomplete), 3 whose meeting was cancelled and never rebooked, and 2
   * already DELIVERED with nothing next — one of them for six months. Only the
   * third is alarming, and under one label it was invisible.
   */
  unscheduledReason: UnscheduledReason

  /**
   * The delivery meeting that was called off and never rebooked, so the row can
   * name the date that was lost. `null` unless `unscheduledReason` is
   * `CANCELLED_NOT_REBOOKED`.
   *
   * **It is not a deadline and never bands the row.** A cancelled meeting is
   * not a commitment; this just stops the row being mute about one.
   */
  droppedMeetingAt: string | null
  /**
   * An IE case's upcoming kickoff — never a deadline, because no work is owed
   * before it, but proof the case is on the calendar (sc-1212 round 9).
   */
  kickoffMeetingAt: string | null
  kickoffMeetingType: string | null
  /** Whether this case's meetings are booked, in order, and on the calendar. */
  sequence: CaseSequence
}

/**
 * Why a case has no delivery meeting booked — mirrors the API's
 * `UnscheduledReason`. Declaration order is urgency order.
 *
 * - `DELIVERED_NOTHING_BOOKED` — the plan went out and nothing is next, past a
 *   five-business-day grace window. This is what the "Missing required
 *   meetings" badge renders on. A case delivered this morning is deliberately
 *   NOT flagged.
 * - `CANCELLED_NOT_REBOOKED` — a meeting existed and was called off.
 * - `NEVER_BOOKED` — nothing was ever on the calendar. The large, normal
 *   bucket; never styled as a problem.
 */
export type UnscheduledReason =
  | 'DELIVERED_NOTHING_BOOKED'
  | 'CANCELLED_NOT_REBOOKED'
  /**
   * An IE case whose KICKOFF is booked, so no work is due yet (sc-1212 r9).
   * Split out of `NEVER_BOOKED` because the two call for opposite actions —
   * this one must never reach the escalate-to-Planning list.
   */
  | 'KICKOFF_BOOKED'
  /**
   * The Pillars half of a Strategic Oversight sale with nothing booked
   * (sc-1212 round 23). An SO sale opens two Case Design tickets; the
   * Rothology half is worked first and the Pillars half only after it closes.
   * So this is a second service that has not started — waiting its turn, NOT a
   * missed or dropped meeting. Resolves after `DELIVERED_NOTHING_BOOKED` and
   * `CANCELLED_NOT_REBOOKED` (a dropped Pillars meeting is still a dropped
   * commitment) and before `NEVER_BOOKED`. Never escalate it.
   */
  | 'AWAITING_SECOND_SERVICE'
  | 'NEVER_BOOKED'
  | null

/**
 * One manager pass over one case — mirrors the API's `CaseLastAudit`.
 *
 * `notePosted` is false whenever `note` is null (there was nothing to post),
 * and ALSO when a note exists but never reached HubSpot. Those are different
 * situations and the hover surface says so: the second means the specialist
 * will never see what the manager wrote.
 */
/**
 * How long a manager asked for a case to stay parked — mirrors the API's
 * `CaseMuteUntilKind` (sc-1212 round 9).
 */
export const CASE_MUTE_UNTIL_KINDS = [
  'DAYS_7',
  'DAYS_14',
  'DAYS_30',
  'DATE',
  'NEXT_MEETING',
] as const

export type CaseMuteUntilKind = (typeof CASE_MUTE_UNTIL_KINDS)[number]

/**
 * A live park on one case — mirrors the API's `CaseMute`.
 *
 * `until` is the expiry ACTUALLY in force. When the park was pinned to a
 * meeting that has since been pushed out, it follows that meeting and
 * `followedMeeting` is true, which the UI says out loud — a manager who parked
 * a row until Tuesday should know it now runs to the following week, and why.
 */
export interface CaseMute {
  id: string
  until: string
  followedMeeting: boolean
  reason: string | null
  actorName: string
  at: string
}

/**
 * The "group" a park on a DELIVERED row is granted against — mirrors the API's
 * `DELIVERED_PARK_GROUPS` (sc-1212 round 9).
 *
 * A delivered case has no {@link ForecastQueueGroup}; it has left the open
 * stages. But it has a grouping a manager sees it in, and that is exactly what
 * the two delivered lists split on: whether a next meeting is booked. Using it
 * as the park signature means booking a meeting (or losing one) moves the row
 * between lists and lapses the park by itself.
 */
export const DELIVERED_PARK_GROUPS = {
  awaiting: 'DELIVERED_AWAITING',
  upcomingWork: 'DELIVERED_UPCOMING_WORK',
  readyToClose: 'DELIVERED_READY_TO_CLOSE',
  /**
   * In "Billable — work done after delivery" (sc-1212 round 14).
   *
   * **The one member of this map the API's copy does not declare yet**, and it
   * is deliberately not one of the three above. A park is granted against the
   * situation a manager was looking at, so parking a case out of the billable
   * review list must not also take it out of Ready to close — those are
   * different judgements about the same case ("not worth invoicing yet" against
   * "not ready to close"), and sharing a group would silently make one lift the
   * other.
   *
   * Safe to send ahead of the API: `ParkCaseDto.group` is a free-form string,
   * so the park is stored either way. Until the API mirrors this value its own
   * lapse check will not match it, which degrades to the park not sticking —
   * never to a failed write.
   */
  postDeliveryWork: 'DELIVERED_POST_DELIVERY_WORK',
} as const

export type DeliveredParkGroup =
  (typeof DELIVERED_PARK_GROUPS)[keyof typeof DELIVERED_PARK_GROUPS]

/** Every group a park can be granted against. */
export type CaseParkGroup = ForecastQueueGroup | DeliveredParkGroup

/**
 * The least a row needs for the park panel to act on it (sc-1212 round 9).
 *
 * Built by the caller from either row shape rather than taking a whole row:
 * the open queue and the delivered lists name their next meeting differently
 * (`deadlineMeeting`/`deadlineAt` against `nextMeeting`), and the panel has no
 * business knowing which it was handed.
 */
export interface CaseParkTarget {
  ticketId: string
  mute: CaseMute | null
  group: CaseParkGroup
  stageId: string | null
  workStarted: boolean
  /** The meeting a NEXT_MEETING park pins to, so it can follow a reschedule. */
  nextMeetingId: string | null
  nextMeetingAt: string | null
}

/**
 * Whether a case's meetings are booked, in order, and on the calendar —
 * mirrors the API's `CaseSequence` (sc-1212 round 10).
 *
 * **This is a hand-maintained COPY of a union that lives in a separately
 * deployed app** (`apps/api/.../analytics/types/case-sequence.types.ts`), so
 * the lists below are only ever as current as the last web deploy. A
 * `Record<CaseSequenceHealth, …>` therefore proves nothing at runtime: when
 * the API shipped `REOPENED` ahead of web, every such lookup returned
 * `undefined` and the icon map took the Forecasts page down with React #130.
 * Keep the members and their ORDER identical to the API's, and read these
 * unions through the resolvers in `CaseSequenceCell.tsx` — never by indexing
 * a `Record` with a value that arrived over the wire.
 */
export type CaseBook = 'ROTHOLOGY' | 'IE'

export type CaseSequenceStepState = 'HELD' | 'BOOKED' | 'DROPPED' | 'MISSING'

export interface CaseSequenceStep {
  key: string
  label: string
  state: CaseSequenceStepState
  at: string | null
  /**
   * A LATER occurrence of this same step, held or booked after `at`.
   *
   * Null for the normal case. Non-null means delivery did not stop here — a
   * second escape plan, a repeat presentation — which is what makes the case
   * `REOPENED`. Recurring steps (the IE quarterly) never set it, because
   * repeating is what they are for.
   */
  repeatAt: string | null
  /**
   * A SECOND meeting of this same step inside the SAME CALENDAR QUARTER
   * (sc-1212 red rows) — the date of the one that should not exist.
   *
   * Tyler caught it on ticket 48619108613 (William Warren): an IE Quarterly
   * held 2026-10-06 and another booked 2026-10-20, both in 2026Q4. You get
   * one quarterly per quarter, so two is wrong by definition.
   *
   * **Not the same as `repeatAt`.** A repeat months later is delivery
   * continuing, which is billable and normal enough to have its own health
   * state; two in one quarter is duplicated work. 250 ticket-quarters on
   * production, 239 of them two meetings actually HELD rather than a
   * reschedule in flight.
   *
   * Optional on the wire like `alerts` below, and for the same reason — see
   * `caseSequenceAlerts`.
   */
  duplicateAt?: string | null
}

/** Worst first, which is also the filter's order. */
export const CASE_SEQUENCE_HEALTHS = [
  'BROKEN',
  'REOPENED',
  'ATTENTION',
  'ON_TRACK',
  'COMPLETE',
  'SHARED',
  'UNKNOWN',
] as const

export type CaseSequenceHealth = (typeof CASE_SEQUENCE_HEALTHS)[number]

/**
 * The things that make a row RED, worst first (sc-1212 red rows).
 *
 * Mirrors the API's `CASE_SEQUENCE_ALERTS` — members and ORDER identical, so
 * the first entry is the one to say out loud when space allows only one.
 *
 * **Read it through `caseSequenceAlertPresentation`, never by indexing a
 * `Record`.** This is the newest copy of an API union on this page, mirrored
 * by a separately-deployed bundle, and it was being added on both sides at
 * once — the exact seam that white-screened this page with React #130 when
 * `REOPENED` arrived here first.
 */
export const CASE_SEQUENCE_ALERTS = [
  /** The same step twice in one calendar quarter. 250 ticket-quarters. */
  'DUPLICATE_MEETING',
  /** Steps held out of sequence, or a later one before an earlier one. */
  'OUT_OF_ORDER',
  /** A step was called off and nothing replaced it. */
  'DROPPED_STEP',
  /** Delivery carried on after the escape plan — see `REOPENED`. */
  'REOPENED_AFTER_DELIVERY',
] as const

export type CaseSequenceAlert = (typeof CASE_SEQUENCE_ALERTS)[number]

export interface CaseSequence {
  book: CaseBook | null
  health: CaseSequenceHealth
  steps: Array<CaseSequenceStep>
  /**
   * Other open cases in the same book sharing these meetings, or 0.
   *
   * Non-zero is why `health` is SHARED: HubSpot associates meetings with
   * CLIENTS, not tickets, so with two open cases there is no fact saying which
   * one a meeting belongs to. The row shows the history and declines to grade.
   */
  sharedWith: number
  /**
   * Why this row is RED, worst first, or empty when it is not (sc-1212).
   *
   * Tyler, 2026-10-09, on the William Warren duplicate: *"This entire row
   * needs highlighted red … id love to have a toggle next to Include annual
   * reviews for ability to filter all tabs to show these red rows."*
   *
   * **One concept, not four colours.** A row can be out of order AND carry a
   * duplicate AND have reopened; a reader needs "this row is wrong" at a
   * glance and the reasons on inspection. `health` keeps grading the case;
   * this says whether to look at it.
   *
   * **Optional here although the API declares it required**, which is not a
   * drift: `apps/web` and `apps/api` deploy separately, so this bundle can be
   * live against an API that has never heard of the field, and a required
   * declaration would be a promise the compiler cannot keep. Every reader
   * goes through `caseSequenceAlerts()`
   * (`analytics/-components/case-sequence-alerts.ts`), which reads an absent
   * or malformed value as "no alerts" rather than crashing a nine-tab page.
   */
  alerts?: Array<CaseSequenceAlert>
}

/** Body for `POST /analytics/cases/:ticketId/park`. */
export interface ParkCasePayload {
  until: CaseMuteUntilKind
  untilDate?: string
  reason?: string
  /** The situation the manager was looking at — the park lapses when it changes. */
  group: CaseParkGroup
  stageId: string | null
  workStarted: boolean
  deadlineMeetingId: string | null
  deadlineAt: string | null
}

/**
 * The least a row needs to carry an audit marker (sc-1212 round 9).
 *
 * Both {@link ForecastQueueRow} and {@link AwaitingNextMeetingRow} satisfy it,
 * which is the point: the marker and its dialog are one component each, so the
 * open queue and the delivered lists cannot drift into showing notes
 * differently. `needsAttention` is optional because only the open queue has
 * the snapshot history that flag is computed from.
 */
export interface CaseAuditTarget {
  ticketId: string
  subject: string | null
  client: { id: string; name: string } | null
  owner: { id: string; userId: string | null; name: string } | null
  lastAudit: CaseLastAudit | null
  daysSinceAudit: number | null
  needsAttention?: boolean
}

export interface CaseLastAudit {
  /** ISO timestamp of the pass. */
  at: string
  kind: 'AUDITED' | 'CHASED'
  note: string | null
  actorName: string
  notePosted: boolean
  notified: boolean
}

// ---------------------------------------------------------------------------
// Case audit (sc-1212 manager tools) — POST /analytics/cases/:ticketId/audit
// ---------------------------------------------------------------------------

/**
 * A manager's pass over one case. Mirrors the API's `CaseAuditKind`/
 * `CASE_AUDIT_KINDS` exactly (`apps/api/.../analytics/types/case-audit.types.ts`).
 *
 * Declaration order is display order. Both quiet the case for the same
 * period — the distinction is for reading the history back, not for the
 * resurfacing rule.
 */
export const CASE_AUDIT_KINDS = ['AUDITED', 'CHASED'] as const
export type CaseAuditKind = (typeof CASE_AUDIT_KINDS)[number]

export const CASE_AUDIT_KIND_LABELS: Record<CaseAuditKind, string> = {
  AUDITED: 'Audited',
  CHASED: 'Chased',
}

/** `POST /analytics/cases/:ticketId/audit` request body. Mirrors the API's
 * `RecordCaseAuditDto` exactly. */
export interface RecordCaseAuditPayload {
  kind: CaseAuditKind
  /** Posted as a note on the HubSpot ticket when present — max 2000 chars.
   * Omit to record the pass with no note. */
  note?: string
  /** Ring this user's bell. Omit to record the pass without notifying — the
   * notification is opt-in per action, never a setting. */
  notifyUserId?: string
}

/**
 * What a recorded pass actually achieved. Mirrors the API's
 * `RecordedCaseAudit` exactly.
 *
 * **`notePosted`/`notified` are separate from "the audit succeeded."** The
 * audit itself always lands if this resolves at all; HubSpot and the bell
 * are best-effort. A caller must say so when a note was given but
 * `notePosted` is false — never show a plain success toast in that case.
 */
export interface RecordedCaseAudit {
  id: string
  createdAt: string
  notePosted: boolean
  notified: boolean
}

/**
 * What closing one case from Ready to close did. Mirrors the API's
 * `CASE_CLOSE_OUTCOMES` exactly, in the same order — best first, so a caller
 * summarising a batch can sort on it.
 *
 * **Four of the five wrote nothing, and only one of those is an error.**
 * `alreadyClosed` is the ordinary result of two managers clearing the same
 * list, and `notReady` is the case that went back into revisions while the
 * page was open. A toast must name them apart from `closed` rather than
 * reporting a move that did not happen.
 */
export const CASE_CLOSE_OUTCOMES = [
  'closed',
  'alreadyClosed',
  'notReady',
  'notFound',
  'failed',
] as const

export type CaseCloseOutcome = (typeof CASE_CLOSE_OUTCOMES)[number]

export interface CaseCloseResult {
  ticketId: string
  outcome: CaseCloseOutcome
  /** The stage HubSpot was actually in at the moment of the write, or null
   * when HubSpot did not return the ticket. */
  stage: string | null
  /** HubSpot's own message, on `failed` only. */
  error?: string
}

export interface CloseCasesResult {
  /** One entry per requested ticket, in the order they were requested. */
  results: Array<CaseCloseResult>
  /** How many actually moved — the number to report. */
  closed: number
}

/**
 * `POST /analytics/cases/close` request body. Mirrors the API's
 * `CloseCasesDto` exactly.
 *
 * **An array even for one row**, because the per-row button and the bulk bar
 * are one act. It carries ids and nothing else: unlike `ParkCasePayload`,
 * which deliberately sends the situation the manager was looking at, the
 * server re-reads the stage from HubSpot — a park only hides a row from the
 * person who parked it, while this writes to a client's ticket.
 */
export interface CloseCasesPayload {
  ticketIds: Array<string>
}

/** The most tickets one close request may carry — mirrors the API's cap. */
export const CASE_CLOSE_MAX_TICKETS = 100

/**
 * The subset of {@link AnalyticsFilters} `GET /analytics/forecast-queue`
 * actually accepts — `experience`/`person` only. A bare `Pick` rather than
 * the whole `AnalyticsFilters`, so a caller can't accidentally believe
 * `range`/`from`/`to`/`department` do anything here (see
 * {@link AnalyticsForecastQueue}'s doc comment for why they don't).
 */
export type ForecastQueueFilters = Pick<
  AnalyticsFilters,
  'experience' | 'person'
>

/**
 * `GET /analytics/forecast-queue` (sc-1212). Mirrors the API's
 * `AnalyticsForecastQueue`
 * (`apps/api/.../analytics/types/forecast-queue.types.ts`) exactly.
 *
 * **A standing snapshot, not a windowed metric** — like
 * {@link OrphanedTickets}, this endpoint deliberately ignores the page's date
 * Range: "which cases need a forecast right now" has no meaningful window.
 * The endpoint accepts `experience`/`person` only, never `range`/`from`/`to`/
 * `department` — sending them would silently do nothing, so the client must
 * not send them at all, and the Forecasts page shows no Range control (see
 * `routes/_app/backoffice/analytics/forecasts/index.tsx`).
 *
 * `urgent`/`scheduled`/`unscheduled` arrive **already sorted** by the
 * server — `urgent` and `scheduled` soonest-due first (by
 * `requiredReadyBy`), `unscheduled` oldest-first. Render them in the given
 * order; do not re-sort by default (column sorting is fine as an opt-in on
 * top, same as `KpiRecordsDialog`'s page-local sort, §11c).
 *
 * **Grouped by `group` (what was promised), independent of `band` (when the
 * case must start).** The three arrays below partition every row by
 * `group`; `counts`/`band` still cut across all three — a rollup-card click
 * filters `urgent`, `scheduled` AND `unscheduled` down to one band each,
 * never picks a section.
 */
export interface AnalyticsForecastQueue {
  generatedAt: string
  /**
   * `group: 'URGENT'` — past the date this case was promised and still
   * open. 22 of 172 on production. The morning list; pre-sorted soonest-due
   * (most overdue) first.
   */
  urgent: { rows: Array<ForecastQueueRow>; total: number }
  /**
   * `group: 'SCHEDULED'` — a date exists (a booked meeting, or the
   * data-gathering budget clock with no meeting) and is still ahead. 56 on
   * production. Pre-sorted soonest-due first.
   */
  scheduled: { rows: Array<ForecastQueueRow>; total: number }
  /**
   * Cases a manager parked — correctly urgent, genuinely not actionable yet
   * (sc-1212 round 9). Its own section and OUT of the other three: a parked
   * case must not inflate the urgent count, and must not vanish either.
   * Soonest-returning first.
   */
  parked: { rows: Array<ForecastQueueRow>; total: number }
  /**
   * `group: 'UNDATED'` — no meeting booked and no data-gathering approval,
   * so nothing has promised this case a date. 94 on production, of which 81
   * have genuinely never had a form approved. Pre-sorted oldest-first — the
   * field keeps its original name (`unscheduled`) though the group it holds
   * is `UNDATED`.
   */
  unscheduled: { rows: Array<ForecastQueueRow>; total: number }
  counts: Record<ForecastQueueBand, number>
  /** How many rows across both sections are Annual Reviews (second priority). */
  annualReviews: number
  /**
   * Of those, how many predate the in-app Annual Review feature — residue from
   * the process it replaced. Reported separately so the page can say "91 cases
   * waiting, plus 80 stale AR tickets to close" rather than one total that
   * means two different things.
   */
  legacyAnnualReviews: number
  /**
   * What `safeStartAt` and `projectedReadyAt` are computed from — see
   * {@link WorkEstimate}. Replaces the per-stage `offsets`.
   *
   * **Read it as possibly absent even though it is typed required.**
   * `apps/web` and `apps/api` deploy separately (the same hazard
   * `postDeliveryWork` documents), so an older API sends `offsets` instead
   * and this is `undefined`. Default it to `[]` at the point of use.
   */
  workEstimates: Array<WorkEstimate>
  /**
   * The turnaround budget in business days — the firm's delivery target,
   * stored in `JourneySchedulingSetting.deliveryTargetBusinessDays` and edited
   * at Backoffice → Journey Scheduling. Every `budgetBusinessDaysLeft` on the
   * rows was counted down against THIS number, which is why the page reads it
   * from here and never from a second request or a literal: two sources would
   * let it print a budget the rows were not computed against. Mirrors the
   * API's `AnalyticsForecastQueue.budgetBusinessDays`.
   *
   * **Read it as possibly absent even though it is typed required.** `apps/web`
   * and `apps/api` deploy separately (the same hazard `postDeliveryWork`
   * documents), so an older API will not send it. Pass it through
   * `caseBudgetBusinessDays`, which returns `null` rather than a guess, and
   * render the number-free wording on null. Never fall back to a literal.
   */
  budgetBusinessDays: number
  /** Clients with an Escape Plan Meeting but no Case Design ticket that could
   * be joined to it — a different fact from a case with no meeting, and
   * excluded from both `scheduled` and `unscheduled` because there is no
   * ticket to attribute a row to. Surfaced as its own count so the two
   * arrays' totals reconcile with what a reader can otherwise account for. */
  unjoinableCases: number

  /**
   * Delivered cases with no next meeting — mirrors the API's
   * `awaitingNextMeeting` (sc-1212 round 7).
   *
   * **These cases are not in the three sections above**, and that is why the
   * list exists. Ready for Presentation is excluded from the queue's open
   * stages because 1,185 cases sit there with a median age of 223 days — right
   * for the queue, but it hid the live ones: a plan delivered, a follow-up
   * expected, nothing on the calendar. Ticket 47633437779 sat like that for 21
   * days with no surface anywhere.
   */
  awaitingNextMeeting: AwaitingNextMeetingSection

  /** Ready cases with a meeting ahead, and when work must be submitted. */
  upcomingDeliveryWork: UpcomingDeliveryWorkSection

  /** Finished cases waiting only on a close — matches `readyCloseOut.readyToClose`. */
  readyToClose: ReadyToCloseSection

  /** What the whole Ready population says about close-out. */
  readyCloseOut: ReadyCloseOutCounts

  /**
   * Cases that kept working after delivery, and what that time is worth —
   * mirrors the API's `postDeliveryWork` (sc-1212 round 14).
   *
   * **Read it as possibly absent even though it is typed required.** `apps/web`
   * and `apps/api` deploy separately, so a web bundle can reach an API that has
   * never heard of this field; every reader here goes through
   * `PostDeliveryWorkSection | undefined` for the same reason the sequence
   * unions go through a resolver.
   */
  postDeliveryWork: PostDeliveryWorkSection
}

/**
 * The rate the firm bills post-delivery work at — mirrors the API's
 * `POST_DELIVERY_HOURLY_RATE`. Tyler, 2026-10-07: *"shows clients by hours to
 * track extra work we should be charging 500 an hour for"*.
 *
 * **Not what a component multiplies by.** The money on screen is always
 * `section.hourlyRate` off the payload, so the figure a reader sees is the one
 * the server computed against. This mirror exists only as the last-resort
 * fallback for a payload that carries rows and no rate — see
 * `postDeliveryHourlyRate` in `forecast-queue-format.ts`.
 */
export const POST_DELIVERY_HOURLY_RATE = 500

/**
 * How a client's planner was decided — mirrors Prisma's `PlannerSource`
 * (sc-1175, surfaced here by sc-1212 round 15).
 *
 * Mirrored as a union rather than imported, for the same reason every other
 * enum on this page is: `@prisma/client` is not reachable from `apps/web`.
 *
 * **It is a wire value, so never index a `Record` with it.** The API deploys
 * separately, a sixth member would arrive here before this bundle knew it, and
 * `SOME_MAP[source].label` on an unrecognised key is a React #130 white screen
 * — which this page has already shipped once. `postDeliveryPlannerSourceReading`
 * is the resolver; it takes a `string`, not this union.
 */
export type PlannerSource =
  /**
   * The legacy `rothology_planner` ticket property, which resolved to a TICKET
   * OWNER — what sc-1175 replaced. A name attributed this way may be a
   * specialist rather than a planner, so the UI marks these rows.
   */
  | 'HUBSPOT_PROPERTY'
  | 'DATA_GATHERING'
  | 'PRESENTATION'
  | 'ESCAPE_PLAN_MEETING'
  | 'MANUAL'

/**
 * One delivery meeting held after the escape plan, with its measured length —
 * mirrors the API's `PostDeliveryWorkMeeting`.
 */
export interface PostDeliveryWorkMeeting {
  meetingId: string
  /** The raw HubSpot activity type, so the row can say WHICH meeting repeated. */
  type: string
  at: string
  /**
   * MEASURED from the meeting's own start and end times, never assumed from a
   * nominal length. Null when HubSpot has no end time — those are counted in
   * `PostDeliveryWorkRow.meetingsMissingDuration` rather than guessed at, which
   * is why the row's `hours` is a floor.
   */
  hours: number | null
}

/**
 * A case that kept working after it was delivered — mirrors the API's
 * `PostDeliveryWorkRow` (sc-1212 round 14).
 *
 * **Not the Ready-stage population** the other three delivered lists draw
 * from. A case that carried on past its escape plan can sit in any stage,
 * including Closed, which is why `stage` is on the row: a closed case still
 * earned the hours, and whether it is still open changes what a manager does
 * about it.
 *
 * The audit, park and sequence sub-types are the same ones
 * {@link AwaitingNextMeetingRow} carries, so `CaseAuditMarker`,
 * `CaseParkPanel`, `ParkedCasesDisclosure` and `CaseSequenceChips` all take
 * these rows unchanged.
 */
export interface PostDeliveryWorkRow {
  ticketId: string
  subject: string | null
  hubspotUrl: string | null
  client: { id: string; name: string } | null
  owner: { id: string; userId: string | null; name: string } | null
  /**
   * The ticket's raw `service_type`, NOT a resolved experience or work kind.
   *
   * That is the reason the page's Experience and Work kind pickers cannot reach
   * this list — see `matchesPostDeliveryWorkFilters`.
   */
  serviceType: string | null
  isAnnualReview: boolean
  /**
   * The HubSpot pipeline stage the case sits in NOW.
   *
   * Read as display text and nothing else: whether the API sends a stage id or
   * a resolved label, `postDeliveryStageReading` renders what arrived and only
   * ever decides "is this one closed" by recognising BOTH spellings.
   */
  stage: string | null
  /**
   * The FIRST held escape plan. The clock starts there, not at the last one —
   * everything after the first delivery is the extra work.
   */
  deliveredAt: string
  /**
   * Delivery meetings HELD strictly after `deliveredAt`.
   *
   * Zero is valid — see {@link PostDeliveryWorkRow.lastMeetingAt}. A case can
   * be on this list purely on its change requests, with nothing yet held.
   */
  extraMeetings: number
  /**
   * Summed measured hours across `meetings`.
   *
   * **A FLOOR, not a total.** Prep and analysis are on nobody's calendar, so
   * this only ever counts meeting time. The UI has to say so rather than
   * present it as the time the case took.
   */
  hours: number
  /** Meetings with no end time in HubSpot — why `hours` is a floor. */
  meetingsMissingDuration: number
  /**
   * Change requests that arrived after `deliveredAt`.
   *
   * **Reported BESIDE the hours and never added into them or into the money.**
   * A rework request is specialist time with no meeting attached, so it has no
   * measured duration; adding an invented figure to a number headed for an
   * invoice is how a billing report stops being trusted. It gets its own
   * column, visibly outside the total.
   */
  reworkRequests: number
  /**
   * The most recent extra meeting — how live this case still is.
   *
   * **Null when no meeting has been HELD since delivery**, which is a real and
   * first-class row rather than a gap. Tyler's own reference case, ticket
   * 46490393746, is one: its escape plan was held 2026-09-14, a change request
   * landed four hours later, and the second escape plan is booked for
   * 2026-10-14 and has not happened. So it has `extraMeetings: 0`, `hours: 0`,
   * `meetings: []`, `lastMeetingAt: null` and `reworkRequests > 0` — and it is
   * the case he pointed at when asking for this tab, so a list that drops it
   * has failed.
   *
   * A booked meeting is deliberately NOT priced: 137 of 520 escape plans are
   * cancelled, so about a quarter of that money would never happen. `hours`
   * stays measured, and these rows carry no money at all.
   */
  lastMeetingAt: string | null
  /** The meetings themselves, so the figure can be audited before invoicing. */
  meetings: Array<PostDeliveryWorkMeeting>
  /**
   * The PLANNER this case's post-delivery work counts against — Tyler's
   * "repeat offenders" (sc-1212 round 15).
   *
   * **NOT the ticket owner, and the UI must never let the two read as one
   * person.** `owner` above is the Case Design SPECIALIST who holds the
   * ticket; this is the client's planner, which is a different job (see the
   * root `CLAUDE.md`: advisor/planner is sales/service, and the specialist is
   * neither). The API resolves it from `Client.plannerId`, which covers 53 of
   * the 54 billable cases and 90% of the money — the ticket's own `planner`
   * HubSpot property is set on 13 and covers 5%.
   *
   * Null for a client with no planner attributed at all — one case today, and
   * it carries the single largest figure on the list (5.0h). It is shown as an
   * unattributed bucket rather than folded into somebody's total.
   */
  planner: { id: string; userId: string | null; name: string } | null
  /**
   * HOW the planner was decided, because it changes how much to trust the row.
   *
   * `ESCAPE_PLAN_MEETING` and the other meeting kinds were attributed from a
   * real meeting by the sc-1175 ladder. `HUBSPOT_PROPERTY` is the legacy
   * `rothology_planner`, which resolved to a TICKET OWNER and is what sc-1175
   * replaced — **so on those rows the name may be a specialist.** Production
   * is 29 meeting-attributed, 24 legacy, 1 none.
   *
   * Read it through `postDeliveryPlannerSourceReading`, never by indexing a
   * `Record` with it: it is an API enum, a member added server-side would
   * arrive here before this bundle knew it, and indexing a wire string is how
   * this page white-screened in production once already.
   */
  plannerSource: PlannerSource | null
  lastAudit: CaseLastAudit | null
  daysSinceAudit: number | null
  mute: CaseMute | null
  sequence: CaseSequence
}

/**
 * Cases billable for work done after delivery — mirrors the API's
 * `PostDeliveryWorkSection` (sc-1212 round 14).
 *
 * **A tab that BUILDS, not one rows are moved into** (Tyler: *"So not a tab to
 * Move clients from this into, a seperate tab that builds"*). A case appears
 * here because of what its calendar says, and it keeps whatever reading it has
 * on the other lists.
 *
 * No window: like everything else on the Forecasts route, the page's Range
 * never reaches this view. The bound is the escape plan, not a date range.
 */
export interface PostDeliveryWorkSection {
  /** Most hours first — the biggest unbilled work at the top. */
  rows: Array<PostDeliveryWorkRow>
  /** Parked rows, out of `rows` — see `AwaitingNextMeetingSection.parked`. */
  parked: Array<PostDeliveryWorkRow>
  /**
   * `POST_DELIVERY_HOURLY_RATE`, so the UI multiplies by a value it was given
   * rather than one it hard-codes.
   */
  hourlyRate: number
}

/** The delivered-and-forgotten watch list — mirrors the API's section. */
export interface AwaitingNextMeetingSection {
  rows: Array<AwaitingNextMeetingRow>
  /**
   * Rows a manager parked, out of `rows` and out of its count.
   *
   * Local to this section rather than pooled into the queue's own `parked`:
   * for a delivered row the group it left IS this list, and the two
   * populations are deliberately separate everywhere else on the page.
   */
  parked: Array<AwaitingNextMeetingRow>
  /** How far back the list reaches, in days since the case reached Ready. */
  windowDays: number
  /**
   * How many MORE such cases sit outside the window — 980 on production.
   *
   * Shown, not hidden: a short list has to be distinguishable from a narrow
   * window, and the size of this number is itself the finding.
   */
  beyondWindow: number
}

export interface AwaitingNextMeetingRow {
  ticketId: string
  /**
   * The last manager pass over this case (sc-1212 round 9) — Tyler:
   * *"i cant use this feature in the delivered sections."*
   *
   * **No `needsAttention` here, deliberately.** That flag comes from the
   * day-to-day snapshots, and the snapshot job only covers the open stages, so
   * a case sitting in Ready has no history to be stale against. The note needs
   * no snapshot, and it was the part a manager wanted.
   */
  lastAudit: CaseLastAudit | null
  daysSinceAudit: number | null
  /** The live park on this case, non-null exactly for a section's `parked` rows. */
  mute: CaseMute | null
  /** The next meeting's HubSpot id, so a park pinned to it follows a reschedule. */
  nextMeetingId: string | null
  /** The per-step meeting audit — the icons on this list. */
  sequence: CaseSequence
  subject: string | null
  hubspotUrl: string | null
  client: { id: string; name: string } | null
  owner: { id: string; userId: string | null; name: string } | null
  /** When the case reached Ready — i.e. when the plan was delivered. */
  deliveredAt: string
  daysSinceDelivered: number

  /**
   * The same three fields a queue row carries, so the page's filters mean one
   * thing across both lists (sc-1212 round 7). Annual Reviews were **36 of the
   * 65 rows** when this was measured — more than half — which is the same
   * reason the queue hides them by default.
   */
  isAnnualReview: boolean
  experiences: Array<RollupExperienceType>
  caseWorkKind: CaseWorkKind
  /**
   * The HubSpot ticket has NO `service_type` at all (sc-1212 round 7).
   *
   * 129 live Case Design tickets — about 9% of the book. They resolve to
   * `UNCLASSIFIED` and carry no experience, so the Experience picker cannot
   * reach them and they are absent from every per-experience rollup.
   *
   * **Not the same as `caseWorkKind === 'UNCLASSIFIED'`**, which is also true
   * of the 9 tickets typed `Orphaned` — a deliberate label for a known state.
   * This is a field nobody filled in, which is the only one of the two
   * somebody can go and close.
   */
  serviceTypeMissing: boolean

  /** The next delivery meeting this case is working towards, if one is booked. */
  nextMeeting: { type: string | null; at: string } | null

  /**
   * When a planner must hand work over for it to be deliverable — null with no
   * meeting to work back from (1,041 of the 1,190 cases parked in Ready).
   */
  submitWindow: SubmitWindow | null

  /**
   * How often a change request has historically arrived before this kind of
   * meeting, as a whole percent — null when there is no published rate (the IE
   * meetings have none).
   *
   * **A base rate for a KIND of meeting, not a prediction about this case.**
   * The copy must not turn it into one.
   */
  workExpectancyPct: number | null

  /** Whether the case looks finished. See {@link CloseOutState}. */
  closeOut: CloseOutState

  /** Which of the three delivery meetings have been held, in sequence order. */
  deliveryMeetingsHeld: Array<string>

  /**
   * When the most recent delivery meeting since the plan went out was held.
   *
   * **Optional, and `null` is a different statement from absent.** `null` means
   * there is no clock to read: no delivery meeting has been held since the plan
   * shipped, or a change request has already been raised since it. Absent means
   * an API that predates the field (the two apps deploy separately) -- render
   * the row without the planner state rather than guessing one.
   */
  lastHeldMeetingAt?: string | null
  /**
   * How long the PLANNER has had the case since that meeting, as a state. After
   * the meeting the client gives feedback and the planner moves the ticket to
   * Revisions Needed or Alternative Scenarios Needed -- the clock is theirs, not
   * the specialist's. `null` / absent as on {@link lastHeldMeetingAt}.
   */
  plannerResponse?: PlannerResponseState | null
  /** Business days the planner has waited since the held meeting. Same null rules. */
  plannerWaitedBusinessDays?: number | null
}

/** Mirrors the API's planner-response states, in the order a reader works them. */
export type PlannerResponseState = 'JUST_HELD' | 'CHASE' | 'LATE' | 'STALLED'

/**
 * Whether a planner can still hand work over in time for the next meeting
 * (sc-1212 round 8) — mirrors the API's `SubmitWindowState`.
 *
 * `CLOSED` is the MAJORITY state on production (108 of 146), because the
 * Rothology deliveries are booked a median 7 days out and the arithmetic needs
 * about 19. That is why it is a filter and a count, never a per-row badge: a
 * badge firing on three quarters of rows stops being read, and the finding is
 * structural rather than anyone's mistake.
 */
export type SubmitWindowState = 'OPEN' | 'CLOSING' | 'CLOSED'

export interface SubmitWindow {
  /** Meeting less five business days — when the plan must be ready. */
  requiredReadyBy: string
  /** The last day a request can be submitted. Null with no publishable stage
   * offset; `requiredReadyBy` still stands, being pure arithmetic. */
  submitBy: string | null
  state: SubmitWindowState
  /** Whole days until the window shuts; negative once closed, null with no
   * `submitBy`. */
  daysUntilClose: number | null
}

/**
 * Whether a case parked in Ready looks finished — mirrors the API's
 * `CloseOutState`.
 *
 * `READY_TO_CLOSE` (39 cases) and `NO_ESCAPE_PLAN_EVER` (1,151) are reported
 * apart on purpose: only the first is a close-out list. You cannot close a case
 * that may still owe the client a meeting.
 */
export type CloseOutState = 'READY_TO_CLOSE' | 'NO_ESCAPE_PLAN_EVER' | 'UNKNOWN'

/**
 * Ready cases with a delivery meeting ahead — the forward schedule (sc-1212
 * round 8).
 *
 * **A different question from `AwaitingNextMeetingSection`, bounded
 * differently.** That asks "who must book a meeting" and is bounded by how
 * recently the case was delivered; this asks "when must a planner hand work
 * over" and is bounded by having a meeting at all. 149 rows against 41 on
 * production, overlapping not at all — one window could not have meant both.
 */
/**
 * Finished cases waiting only on somebody closing the ticket (sc-1212 r13).
 *
 * **Exempt from the 14-day window the other delivered lists live under.** A
 * close-out case was delivered months ago with nothing booked, so that window
 * threw it out — the count said 39 beside a list that could show six.
 */
export interface ReadyToCloseSection {
  /** Oldest delivery first — cleared from the top. */
  rows: Array<AwaitingNextMeetingRow>
  parked: Array<AwaitingNextMeetingRow>
}

export interface UpcomingDeliveryWorkSection {
  /** Soonest meeting first — the window that shuts next. */
  rows: Array<AwaitingNextMeetingRow>
  /** Parked rows, out of `rows` — see `AwaitingNextMeetingSection.parked`. */
  parked: Array<AwaitingNextMeetingRow>
  /** Mirrors `SUBMIT_WINDOW_CLOSING_DAYS` so the UI can name the band. */
  closingDays: number
}

/**
 * What the whole Ready population says about close-out — counts, not rows.
 *
 * 1,190 cases sit in Ready; listing them is the graveyard the bounded sections
 * exist to avoid. Reported before the page's person and experience pickers,
 * like `beyondWindow`: a statement about the book, not the reader's slice.
 */
export interface ReadyCloseOutCounts {
  total: number
  /** Escape plan held, sequence finished, ticket still open. ~39. */
  readyToClose: number
  /**
   * No escape plan meeting ever. ~1,151 of 1,190.
   *
   * **NOT a close-out list** — either it was never booked or it happened and
   * was never logged. Held meeting evidence explains barely 3% of this backlog,
   * which is why the two counts are never summed.
   */
  noEscapePlanEver: number
}

// ---------------------------------------------------------------------------
// Annual Reviews card (sc-1212) — GET /analytics/annual-reviews
// ---------------------------------------------------------------------------

/**
 * Mirrors the API's `AnnualReviewQueueCounts`
 * (`apps/api/.../analytics/types/annual-reviews.types.ts`) exactly. A live
 * review, by the status the app records.
 */
export interface AnnualReviewQueueCounts {
  queued: number
  upcoming: number
  inProgress: number
  /** queued + upcoming + inProgress — the number a manager reads as
   * "active". */
  active: number
}

/**
 * A duration summary that always carries its sample size. Mirrors the API's
 * `AnnualReviewDuration` exactly — render `n` beside every median/p75 pulled
 * from this shape; a duration without its sample size invites a reader to
 * treat a dozen cases as settled fact.
 */
export interface AnnualReviewDuration {
  medianDays: number | null
  p75Days: number | null
  sampleSize: number
}

/**
 * Reviews coming due. Mirrors the API's `AnnualReviewUpcoming` exactly.
 * Windows are CUMULATIVE — a client due in 20 days counts toward d30, d60
 * AND d90 — never re-derive a "due in this bucket only" figure from these.
 */
export interface AnnualReviewUpcoming {
  d30: number
  d60: number
  d90: number
  /** Clients with an escape plan date — everyone who owes a review at all. */
  population: number
}

/**
 * Mirrors the API's `AnnualReviewFollowOn` exactly — what happened AFTER a
 * review was delivered.
 *
 * **The conversion signal is an IE KICKOFF, not any IE-prefixed meeting**
 * (Tyler, 2026-09-29). The old `ILIKE 'IE%'` filter swept in `IE Quarterly
 * Meeting`, which is service owed to an IE client already sold — 27 of 66
 * matches on production, inflating the count from 23 to 31 with pure
 * service delivery. `withIeKickoff` counts only `IE Kickoff Meeting`, which
 * first appears 2026-08-10 — expect a small number (2 on production) and
 * say why in the UI rather than let it read as "we convert nobody."
 *
 * **No money on this card.** `collectedAfter`/`paymentsAfter` are gone —
 * every payment landing after a review with no experience filter, none of
 * it identifiably IE (invoices carry no IE identifier at all), so the
 * figure could not be fixed, only removed.
 */
export interface AnnualReviewFollowOn {
  completedReviews: number
  /** Reviews followed by an IE KICKOFF — the conversion, not the service. */
  withIeKickoff: number
  /** Breakdown by the IE meeting type, largest first. */
  byMeetingType: Array<{ meetingType: string; reviews: number }>
  /**
   * Reviews whose client completed an IE checkout AFTER that review — bounded,
   * unlike the historical "ever completed IE" version of this figure.
   */
  clientsWithIe: number
}

/**
 * One analyst's Annual Review workload and throughput. Mirrors the API's
 * `AnnualReviewAnalyst` exactly.
 */
export interface AnnualReviewAnalyst {
  userId: string | null
  name: string
  avatarUrl: string | null
  queued: number
  inProgress: number
  completed: number
  /** Of `completed`, how many the app actually recorded this person
   * against. */
  completedRecorded: number
  /**
   * Of `completed`, how many were INFERRED — deduced from the matching Case
   * Design ticket owner or the forecast creator, not recorded by the app.
   * Kept as its own number rather than folded into `completed` so a reader
   * can always see how much of a person's history is measured and how much
   * is deduced. Render in a visibly lighter treatment than
   * `completedRecorded`, never presented as a recorded count.
   */
  completedInferred: number
  turnaround: AnnualReviewDuration
  /** This person's own Queue → In progress median, so the Time-to-start chart
   * can plot the metric it is named after rather than substituting volume. */
  timeToStart: AnnualReviewDuration
}

/**
 * How much Annual Review work the `annual_reviews` table can actually see.
 * Mirrors the API's `AnnualReviewTicketCoverage` exactly.
 *
 * Every metric on this card is built from that table, so its coverage is
 * the card's own margin of error. On production 2026-09-29: 371 Case Design
 * tickets carry the Annual Review service type, and only 58 are linked to a
 * review row — the other 313 (mostly closed, i.e. already delivered) were
 * created by hand and never opened a review. `legacyTickets` is the
 * ACTIONABLE subset of that gap (open tickets with no review, no active
 * review either); the rest is history, reported here rather than fixed —
 * deliberately not backfilled, since a backfill would invent `queuedAt`/
 * `startedAt` timings that never existed and every duration on this card
 * would inherit the fiction.
 */
export interface AnnualReviewTicketCoverage {
  /** Live Case Design tickets carrying the Annual Review service type. */
  arTickets: number
  /** Of those, how many a review row points at. */
  linkedToReview: number
}

/**
 * `GET /analytics/annual-reviews` (sc-1212) — the Annual Reviews card,
 * between the Meetings card and the charts on the Specialists view. Mirrors
 * the API's `AnalyticsAnnualReviews`
 * (`apps/api/.../analytics/types/annual-reviews.types.ts`) exactly.
 *
 * **Takes no query params at all** — not range, not experience, not person.
 * It is a standing firm-wide snapshot, like {@link OrphanedTickets} and
 * {@link AnalyticsForecastQueue} — never wire it to the page's filter card.
 *
 * **`timeToStart.medianDays` can legitimately be `0`.** Queueing and
 * starting are one action in the current process, so a p50 of zero is a
 * real measurement, not a bug or a missing value — render it as `0`, with a
 * caption explaining why, never suppressed.
 *
 * **`analysts[].completedRecorded` + `completedInferred` do not sum to
 * `followOn.completedReviews`** across the roster — see
 * `unattributableReviews` below for the gap and why it exists.
 */
export interface AnalyticsAnnualReviews {
  generatedAt: string
  counts: AnnualReviewQueueCounts
  /** Queued → started. Production measures a p50 of ZERO days — see this
   * interface's doc comment. */
  timeToStart: AnnualReviewDuration
  /** Started → completed: the turnaround a manager actually manages. */
  turnaround: AnnualReviewDuration
  upcoming: AnnualReviewUpcoming
  followOn: AnnualReviewFollowOn
  analysts: Array<AnnualReviewAnalyst>
  /**
   * Completed reviews no analyst signal can name — deliberately NOT
   * attributed by meeting host (a planner, not the specialist/analyst who
   * did the Case Design work). Render as its own caption near the
   * per-analyst list so a reader knows why those totals don't sum to
   * `followOn.completedReviews`, rather than assuming the card is broken.
   */
  unattributableReviews: number
  /**
   * Open Case Design AR tickets with no row in this table — residue from
   * the process this feature replaced, not work in this queue. Render as
   * its own caption, never folded into `counts`.
   */
  legacyTickets: number
  /** See {@link AnnualReviewTicketCoverage} — the card's own margin of error. */
  ticketCoverage: AnnualReviewTicketCoverage
}

/**
 * The five Annual Reviews rollup tiles a reader can drill into (sc-1212 UX
 * pass). Mirrors the API's `ANNUAL_REVIEW_METRICS`
 * (`apps/api/.../analytics/types/annual-reviews.types.ts`) exactly — and, not
 * by accident, is exactly the five `StatTile`s `AnnualReviewsCard` already
 * renders, so a tile IS a metric key: clicking one selects it as the card's
 * current view, clicking it again clears the selection back to By analyst.
 */
export const ANNUAL_REVIEW_METRICS = [
  'activeQueue',
  'turnaround',
  'timeToStart',
  'comingDue',
  'ieKickoffs',
] as const

export type AnnualReviewMetric = (typeof ANNUAL_REVIEW_METRICS)[number]

/**
 * One row behind an Annual Reviews rollup tile. Mirrors the API's
 * `AnnualReviewRecordRow` exactly.
 *
 * `reviewId`/`reviewYear`/`status`/`analyst`/`queuedAt`/`startedAt`/
 * `completedAt` are all `null` for a `comingDue` row — that metric drills
 * into CLIENTS owed a review, not reviews in flight, so there is no review to
 * name yet. `dueAt` is the mirror image: only `comingDue` ever sets it.
 *
 * `value`'s meaning depends on `metric` — days for `turnaround`/
 * `timeToStart`, days until due for `comingDue`, always `null` for
 * `activeQueue`/`ieKickoffs` (neither has a per-row number to show; `ieKickoffs`
 * is the wire key behind the "IE Kickoffs" tile — see `ANNUAL_REVIEW_METRICS`).
 */
export interface AnnualReviewRecordRow {
  reviewId: string | null
  client: { id: string; name: string } | null
  reviewYear: number | null
  status: string | null
  analyst: string | null
  /** Whether `analyst` was recorded by the app or deduced — must render
   * differently (never look like a recorded name), same rule the card's own
   * `completedInferred` qualifier already follows. */
  analystInferred: boolean
  queuedAt: string | null
  startedAt: string | null
  completedAt: string | null
  dueAt: string | null
  value: number | null
}

/** `GET /analytics/annual-reviews/records` (sc-1212 UX pass) — the rows
 * behind one rollup tile, optionally narrowed to one bar/segment of its
 * chart via `groupKey`. Mirrors the API's `AnnualReviewRecords` exactly. */
export interface AnnualReviewRecords {
  metric: AnnualReviewMetric
  groupKey: string | null
  total: number
  page: number
  pageSize: number
  rows: Array<AnnualReviewRecordRow>
}

/**
 * The Cleaning tab's finding kinds (sc-1212 audit round) — mirrors the API's
 * `CLEANING_FINDING_KINDS` (`apps/api/.../analytics/types/cleaning.types.ts`).
 *
 * **This is a hand-maintained copy of an API union in a separately-deployed
 * bundle, so nothing may index a `Record` keyed by it without a fallback.**
 * That is not a theoretical caution: a newer API shipping a kind this bundle
 * has never heard of is exactly how `REOPENED` white-screened the Forecasts
 * page with React #130. Every presentation lookup goes through
 * `cleaningKindPresentation` in `-components/cleaning-format.ts`, which
 * resolves an unrecognised kind to a neutral reading rather than
 * `undefined` — same contract `caseSequenceHealthPresentation` already holds.
 *
 * Order matters: it is the badge/chip order on screen, and it mirrors the
 * API's own declaration order, which leads with `UNTYPED_TICKET` because an
 * unlabelled ticket is invisible to every typed rule meant to catch it.
 */
export const CLEANING_FINDING_KINDS = [
  'UNTYPED_TICKET',
  'DUPLICATE_ROTHOLOGY_TICKET',
  'MEETING_ENDS_BEFORE_START',
  'UNMAPPABLE_MEETING',
  'OUT_OF_ORDER_SEQUENCE',
  'IE_SERVICE_WITHOUT_TICKET',
  /**
   * IE delivery meetings HELD against a deal that was never won, or against no
   * IE deal at all — the MONEY half of `IE_SERVICE_WITHOUT_TICKET`, and the
   * worse half.
   *
   * **Two kinds rather than one because the action differs**: a won deal needs
   * a ticket raised, an unwon one needs somebody to ask why we delivered. Only
   * a handful of the contacts with IE service and no IE ticket were ever paid
   * for — those keep the kind above; the great majority are this one, and tens
   * of thousands of dollars of open-stage IE deals have already had service
   * against them. The API's `CLEANING_FINDING_KINDS` entry
   * (`apps/api/.../analytics/types/cleaning.types.ts`) holds the per-stage and
   * per-contact measurement; it is deliberately not restated here, so the two
   * copies cannot drift on a number — and it already moved once between the
   * ask and the API landing.
   *
   * Same row shape as the kind above: reported per CONTACT, so `ticketId` is
   * not the case the finding is about and `relatedIds` carries meeting ids.
   */
  'IE_DELIVERY_WITHOUT_PAID_DEAL',
] as const

export type CleaningFindingKind = (typeof CLEANING_FINDING_KINDS)[number]

/**
 * The kinds that carry {@link CleaningFinding.deal} — mirrors the API's
 * `IE_DEAL_CLEANING_KINDS`.
 *
 * Here beside {@link CLEANING_FINDING_KINDS} rather than in the component that
 * renders the deal, and typed to {@link CleaningFindingKind} rather than to
 * `string`, so a kind added to the union above cannot quietly fail to be
 * classified. A loose `ReadonlyArray<string>` in a component compiles happily
 * while meaning nothing.
 *
 * **A subset, not a second source of truth**: the API decides whether a row
 * gets a `deal`, and it gates on its own copy. This one only decides whether
 * the web bothers to look, so the worst a drift can do is render no deal row
 * for a kind the API started sending one for — never a link built from
 * nothing.
 */
export const IE_DEAL_CLEANING_KINDS: ReadonlyArray<CleaningFindingKind> = [
  'IE_SERVICE_WITHOUT_TICKET',
  'IE_DELIVERY_WITHOUT_PAID_DEAL',
]

/**
 * One problem to fix, as a row — mirrors the API's `CleaningFinding`.
 *
 * Three fields do NOT mean the same thing on every row, and the table has to
 * say so rather than render a column that quietly lies:
 *
 * - **`ticketId` is genuinely null on a large share of rows.** 56 of the 62
 *   `MEETING_ENDS_BEFORE_START` findings on production carry a null
 *   `ticketId`, `client`, `owner` AND `planner` — they are meetings logged
 *   against contacts with no client record. The nullability is load-bearing,
 *   not defensive, and such a row still has to read usefully: `detail` and
 *   `relatedIds` are its whole content.
 * - **For the two IE kinds, `ticketId` is not the case the finding is about.**
 *   `IE_SERVICE_WITHOUT_TICKET` and `IE_DELIVERY_WITHOUT_PAID_DEAL` are both
 *   reported per CONTACT, because the absence of an IE ticket — or of a won
 *   deal behind the service — IS the finding; `ticketId` names the *Rothology*
 *   ticket the IE meetings landed on instead, where there is one.
 * - **`relatedIds` changes object type by kind** — sibling TICKET ids for
 *   `DUPLICATE_ROTHOLOGY_TICKET`, MEETING ids for the meeting kinds.
 *
 * `detail` names the specific instance ("Booked as \"IE Meeting\" on
 * 2026-09-14"); the badge carries the category. They are never rendered at
 * the same weight, because the badge is scanned and the detail is read.
 *
 * `occurredAt` arrives ISO with a `+00:00` offset and is already correct —
 * it is formatted through `formatReportingDate` (America/Denver), the same
 * zone the API's own detail sentences use, so the date on the row matches
 * the date in the sentence beside it and the date HubSpot showed the reader.
 * Never apply offset arithmetic of your own on top of it.
 */
export interface CleaningFinding {
  /** Stable across requests — `"<kind>:<subjectId>"`, never a row index, so
   * paging or filtering does not reshuffle React keys. */
  id: string
  kind: CleaningFindingKind
  /** Null for a finding about a meeting that reached no ticket — see the
   * interface doc comment. */
  ticketId: string | null
  subject: string | null
  hubspotUrl: string | null
  client: { id: string; name: string } | null
  /** The Case Design SPECIALIST, off the ticket. **Not the planner** — two
   * different jobs, and this console has reported one as the other out loud
   * once already. `name` reads "Unmapped owner" for a departed employee,
   * which is a real finding rather than a defect. */
  owner: { id: string; userId: string | null; name: string } | null
  /** `Client.plannerId` — the client's planner, never the ticket owner. */
  planner: { id: string; userId: string | null; name: string } | null
  detail: string
  occurredAt: string | null
  relatedIds: Array<string>
  /**
   * The meeting this finding is about, where it is about one. Null on
   * `UNTYPED_TICKET` and `DUPLICATE_ROTHOLOGY_TICKET`.
   *
   * **Optional on purpose, not just nullable.** API and web deploy separately
   * from one merge, so this bundle runs for a while against an API that does
   * not send the field at all. Absent, null and "no meeting" must all render
   * the same plain-id row. `contactIds[0]` is what the HubSpot deep link uses
   * (`buildHubSpotMeetingUrl`); without one there is no link.
   */
  meeting?: CleaningFindingMeeting | null
  /**
   * The contact's IE deal, on `IE_SERVICE_WITHOUT_TICKET` and
   * `IE_DELIVERY_WITHOUT_PAID_DEAL` only.
   *
   * **Optional for the same reason as `meeting`** — the API and web deploy
   * separately from one merge, so absent must render exactly as it did before
   * the field existed. Null is a real reading, not a gap: a contact with no IE
   * deal at all, which on `IE_DELIVERY_WITHOUT_PAID_DEAL` is the complement of
   * "won" rather than an error. Null renders no link.
   */
  deal?: CleaningDeal | null
}

/** The IE deal projection carried on a {@link CleaningFinding}. */
export interface CleaningDeal {
  /** HubSpot deal id, for the deep link (`buildHubSpotDealUrl`). */
  id: string
  /** Friendly label for a mapped stage; the literal `stage <id>` for an
   * unmapped one (render verbatim, never substitute); null ONLY when the deal
   * carries no stage id at all. */
  stageLabel: string | null
  /** Whole dollars as TEXT (a Decimal column); often null. */
  amount: string | null
}

/** The meeting projection carried on a {@link CleaningFinding}. */
export interface CleaningFindingMeeting {
  hubspotMeetingId: string
  contactIds: Array<string>
  title: string | null
  meetingType: string | null
  startTime: string | null
  endTime: string | null
  outcome: string | null
  ownerName: string | null
}

/**
 * One page of findings — mirrors the API's `CleaningPage`.
 *
 * `total` matches the CURRENT filters (so the pager and the tab count agree),
 * while `counts` is per-kind under the current NON-kind filters (so a kind
 * chip can act as both a count and a filter button without its number
 * changing meaning the moment it is selected).
 *
 * `counts` is a `Record` keyed by an API enum, so **read it through
 * `cleaningCountFor`, never `counts[kind]`** — a kind this bundle knows but
 * an older API does not returns `undefined`, and `undefined` rendered into a
 * chip is a blank where a number belongs.
 */
export interface CleaningPage {
  rows: Array<CleaningFinding>
  total: number
  counts: Record<CleaningFindingKind, number>
  page: number
  pageSize: number
}

/**
 * The filters `GET /analytics/cleaning` accepts — deliberately NOT
 * `AnalyticsFilters`.
 *
 * The endpoint takes no `range` (this is a standing audit of everything
 * broken right now, and a window would hide the oldest problems — the ones
 * nobody has dealt with) and no `experience` (it resolves to a service-type
 * predicate, which would drop every untyped ticket, the largest kind and the
 * whole reason the tab exists). Sending either would be dead weight at best
 * and a filter that silently empties the list at worst.
 *
 * Empty arrays mean "every kind / every person" — the same permissive
 * convention every filter on the Forecasts page uses — and are omitted from
 * the query string rather than sent as `""`, which would trip the DTO's
 * `@ArrayNotEmpty`.
 */
export interface CleaningFilters {
  kind: Array<CleaningFindingKind>
  /** Case Design specialists, by Rothology user id. */
  owner: Array<string>
  /** Clients' planners, by Rothology user id. */
  planner: Array<string>
  search: string
  /**
   * Inclusive floor on the finding's MEETING date (the `When` column), as a
   * `YYYY-MM-DD` Denver calendar date. ABSENT when no cutoff is set, never
   * `''` or null: the API runs `forbidNonWhitelisted`, so an unknown or
   * malformed param is a 400, and the two ship separately.
   *
   * Meeting-dated kinds only; `UNTYPED_TICKET` and `DUPLICATE_ROTHOLOGY_TICKET`
   * are dated by ticket creation and are never hidden by it.
   */
  since?: string
}
